/**
 * AirFlux fitment widget (§5.5).
 *
 * One component, three scopes: `deflectors`, `wipers`, `all`. The vehicle entry
 * stores the two product lists separately, so scoping is just choosing which
 * field to read — no extra queries, no extra data.
 *
 * Data sources, and why:
 *   · assets/fitment-index.json — the car→product map. Static, on Shopify's CDN,
 *     cached across sessions. No API token, no CORS, no rate limit.
 *   · /products/{handle}.js — live price, stock and variant id for the RESOLVED
 *     products only. Native, tokenless, and always current, so availability can
 *     never go stale the way a session-cached copy would (§8).
 *   · /cart/add.js — the final word on whether something is buyable (§8).
 *
 * No build step, no dependencies.
 */
(() => {
  'use strict';

  const CAR_KEY = 'airflux:car';
  const INDEX_KEY = 'airflux:index';
  const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

  /**
   * The two categories, and the page that sells each one.
   *
   * `t` is the product type as the index writes it. The paths are the store's
   * two landing pages, which are the only surface allowed to sell — the router
   * hands off to them rather than growing a second buying surface of its own.
   */
  const ROUTE = {
    d: { noun: 'Wind deflectors', page: '/pages/wind-deflectors', scope: 'deflectors' },
    w: { noun: 'Wiper blades', page: '/pages/wiper-blades', scope: 'wipers' },
  };

  // Inline rather than referenced: these land inside `innerHTML` on markup the
  // engine writes, where an `{% render 'airflux-icon' %}` cannot reach. Sized in
  // `em` so each one takes the size of the line it sits on.
  const ARROW =
    '<svg class="af-i" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path d="M3 8h10M9 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const CHECK =
    '<svg class="af-i" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path d="M3 8.5l3.5 3.5L13 5" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const INFO =
    '<svg class="af-i" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" stroke-width="1.25"/><path d="M8 7.25v3.5M8 5.1v.9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
  // The same cart as `assets/airflux-icon-cart.svg`, inlined because this button
  // is built in JavaScript and cannot reach `{% render 'airflux-icon' %}`. The
  // product page renders that snippet properly, through the `icon` parameter on
  // `buy-buttons` — 🔴 if the artwork changes, change it in both places.
  const CART =
    '<svg class="af-i" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false"><path d="M2.5 3.5h2.1l2.4 11.2a1.8 1.8 0 0 0 1.76 1.42h8.7a1.8 1.8 0 0 0 1.75-1.37L21 7.6H6.1" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9.6" cy="20" r="1.5" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="17.6" cy="20" r="1.5" fill="none" stroke="currentColor" stroke-width="1.7"/></svg>';

  // The shield beside the guarantee line, traced from
  // `assets/airflux-icon-guarantee.svg` — the product page renders that file
  // through `airflux-icon`. 🔴 If the artwork changes, change it in both places.
  // Wrapped in the same `<span class="af-icon …">` the snippet emits, so one CSS
  // rule sizes both.
  const SHIELD =
    '<span class="af-icon af-buy-assure__icon" aria-hidden="true"><svg viewBox="0 0 22 22" fill="none" focusable="false"><path d="M11 2.75L17.875 5.5V10.45C17.875 14.3917 15.125 17.9667 11 19.25C6.875 17.9667 4.125 14.3917 4.125 10.45V5.5L11 2.75Z" stroke="currentColor" stroke-width="1.8333" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.25 10.8167L10.2667 12.8333L14.1167 8.98333" stroke="currentColor" stroke-width="1.8333" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';

  /** A scroll asked for in script ignores `scroll-behavior`, so this is read. */
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Warms a page the customer is about to be offered.
   *
   * The router hands off to a landing page rather than answering in place, and
   * the one cost of that is a navigation. Prefetching pays it while the customer
   * is still reading the two rows. Deduped, and skipped entirely where the
   * browser has told us the connection cannot afford it.
   */
  const prefetched = new Set();
  function prefetch(href) {
    if (!href || prefetched.has(href)) return;
    const net = navigator.connection;
    if (net?.saveData || /2g/.test(net?.effectiveType ?? '')) return;
    prefetched.add(href);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'document';
    link.href = href;
    document.head.appendChild(link);
  }

  /**
   * Model names, split into comparable tokens.
   *
   * ⚠️ Split on separators, never stripped of them. The old matcher compared
   * `s.replace(/[^a-z0-9]/g,'')`, which glues neighbouring tokens together and
   * invents matches across the join: "A-Class" became "aclass", which *contains*
   * "cla", so a Mercedes CLA resolved to an A-Class. Tokens keep the boundary.
   */
  const modelTokens = (s) =>
    String(s || '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .split(/[^A-Z0-9]+/)
      .filter(Boolean);

  /**
   * Comparison key for a make — accents FOLDED, not dropped.
   *
   * 🔴 This must stay identical to `normaliseKey` in scripts/lib/makes.mjs, and
   * the copy here had drifted: it was `.replace(/[^a-z0-9]/g,'')` alone, which
   * DELETES the accented letter instead of folding it. The catalogue writes
   * "Citroën" and "Škoda"; the register writes "CITROEN" and "SKODA". Those
   * normalised to "citron" vs "citroen" and "koda" vs "skoda", so no Citroën and
   * no Škoda could EVER be found by plate — 40 cars, 7.5% of the catalogue,
   * silently unreachable.
   */
  const makeKey = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]/g, '');

  /**
   * How specifically a catalogue model matches a provider model — the number of
   * tokens matched, or -1 for no match.
   *
   * The catalogue name must be a leading run of the provider's name. Model names
   * lead and trim follows ("X5 SPORT D AUTO", "KUGA TITANIUM TDCI"), so a prefix
   * test absorbs trim without ever matching a car by its trailing words.
   *
   * The direction matters and is the whole point:
   *   ours "Transit"          vs "TRANSIT CONNECT" →  1  (candidate)
   *   ours "Transit Connect"  vs "TRANSIT CONNECT" →  2  (wins — more specific)
   *   ours "Transit Connect"  vs "TRANSIT"         → -1  (never: the register
   *        named the plain van, so the Connect must NOT claim it)
   *   ours "RS 3"             vs "S3"              → -1  (was a match)
   *
   * ⚠️ A prefix is only ever a *candidate*. Where the Worker recognised the
   * model it hands back the whole canonical name, and `resolvePlate` then
   * demands the whole name — because at that point a prefix is a DIFFERENT car:
   * a Citroën C4 Picasso is not a C4, and a Tiguan Allspace is not a Tiguan.
   * Prefix matching survives only as the fallback for a model the vocabulary
   * has never heard of, where the trailing words really might be trim.
   */
  const modelMatchLen = (ourModel, dvToks) => {
    const ours = modelTokens(ourModel);
    if (!ours.length || ours.length > dvToks.length) return -1;
    for (let i = 0; i < ours.length; i++) if (ours[i] !== dvToks[i]) return -1;
    return ours.length;
  };

  /**
   * "MAKE MODEL" as the register describes it, without saying the make twice.
   *
   * The DVLA model field repeats the make on some cars — a base Mini is
   * make "MINI", model "MINI", which printed as "MINI MINI 2011" and reads as a
   * bug to both the customer and the client reading the demand signal. Also
   * covers "MINI MINI COOPER" and "BMW X5".
   *
   * Only strips a COMPLETE make match, so a two-word make can't half-match:
   * "MERCEDES-BENZ" against a model of "C 220 CDI" leaves the model alone.
   * Display only — matching never sees this string.
   */
  const plateCarLabel = (car) => {
    const make = String(car.make || '').trim();
    const model = String(car.modelRaw || car.model || '').trim();
    const mkToks = modelTokens(make);
    const words = model.split(/\s+/).filter(Boolean);
    let i = 0;
    while (i < mkToks.length && i < words.length && modelTokens(words[i])[0] === mkToks[i]) i++;
    const rest = i === mkToks.length ? words.slice(i) : words;
    return [make, ...rest].join(' ').trim();
  };

  /**
   * Fires a DOM event and mirrors it to dataLayer when one exists (§30).
   *
   * 🔴 The two halves have different rules. The DOM event is internal — it goes
   * to other code in this theme and leaves nothing behind — so it always fires.
   * The dataLayer push does not: the only thing that ever reads
   * `window.dataLayer` is a tag manager, and a tag manager is analytics that
   * writes cookies. A container installed through Shopify's custom-pixel
   * manager is gated by Shopify itself; one pasted into the theme or an app
   * embed is NOT, and that is the gap this closes. Costs nothing today — there
   * is no container on this store — and means there is no day on which adding
   * one quietly starts feeding it without consent.
   */
  const track = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`airflux:${name}`, { detail, bubbles: true }));
    if (window.AirfluxStorage?.may('analytics') && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: `airflux_${name}`, ...detail });
    }
  };

  // ── the chosen car, remembered site-wide (§5.5) ────────────────────────────
  /* 🔴 Storage goes through `window.AirfluxStorage`, never `localStorage`
     directly. The car lives in the SESSION unconditionally — that copy is
     exempt under PECR Schedule A1 para 4(2)(e)(ii) as "a record of selections
     made on a website … by the user" — and is mirrored to `localStorage` only
     where the visitor has consented to Preferences, because the ICO's guidance
     is explicit that preference storage which persists "beyond the user's
     current session" needs consent. `snippets/airflux-storage.liquid` holds the
     full reasoning and does the migrating in both directions.

     The practical consequence, which is the point: a shopper who rejects
     everything still keeps their car for the whole visit. Nothing below has to
     know that, because the module answers `get` from the session either way. */
  const Car = {
    get() {
      return window.AirfluxStorage?.readJSON(CAR_KEY) || null;
    },
    set(car) {
      window.AirfluxStorage?.writeJSON(CAR_KEY, car);
      // Mirrored onto the cart so the car reaches the order (§5.5). Built by
      // the shared module so the attribute says the same thing as the line
      // items — "BMW 3 Series" on its own spans three generations.
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: window.AirfluxOrder.cartAttributes(car) }),
      }).catch(() => {});
      document.dispatchEvent(new CustomEvent('airflux:car-changed', { detail: car, bubbles: true }));
    },
    clear() {
      window.AirfluxStorage?.remove(CAR_KEY);
      document.dispatchEvent(new CustomEvent('airflux:car-changed', { detail: null, bubbles: true }));
    },
  };

  // ── the index ──────────────────────────────────────────────────────────────
  let indexPromise = null;

  function loadIndex(url) {
    if (indexPromise) return indexPromise;

    // Keyed on the asset URL, which carries Shopify's `?v=` cache-buster. The
    // sync republishes the index on every run, so a fixed key meant an open tab
    // kept a stale copy for the whole session — and a handle minted after that
    // copy was taken resolves to nothing.
    const cacheKey = `${INDEX_KEY}:${url}`;

    indexPromise = (async () => {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          return JSON.parse(cached);
        } catch {
          sessionStorage.removeItem(cacheKey);
        }
      }

      // Retry twice with backoff, then let the caller show a retry state —
      // never a blank widget, never a console-only error (UX §7.4).
      let lastError;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const res = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          try {
            // Drop any older copy so the quota isn't spent on stale versions.
            for (const k of Object.keys(sessionStorage))
              if (k.startsWith(`${INDEX_KEY}:`) && k !== cacheKey) sessionStorage.removeItem(k);
            sessionStorage.setItem(cacheKey, JSON.stringify(data));
          } catch {
            /* quota — fine, we just refetch next page */
          }
          return data;
        } catch (err) {
          lastError = err;
          if (attempt < 2) await new Promise((r) => setTimeout(r, 300 * 2 ** attempt));
        }
      }
      indexPromise = null; // allow a manual retry to try again
      throw lastError;
    })();

    return indexPromise;
  }

  // An alt Shopify invented rather than a person wrote. Bulk imports leave the
  // file's own UUID in the field, and this catalogue has them — shipped as-is
  // they are what a screen reader reads out for the photograph.
  const UUID_ALT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  /**
   * A `/products/{handle}.js` payload, as slides for `<airflux-gallery>`.
   *
   * Every image the product has, in the merchant's order. Falls back to the
   * featured image alone for a payload with no media list, which is what a
   * product mid-import looks like.
   */
  function galleryMedia(product) {
    const shots = (product.media ?? []).filter((m) => m.media_type === 'image');
    const source = shots.length
      ? shots
      : product.featured_image
        ? [{ src: product.featured_image, preview_image: null, alt: null }]
        : [];

    return source.map((m) => {
      const alt = (m.alt ?? '').trim();
      return {
        src: m.src,
        width: m.preview_image?.width ?? m.width ?? null,
        height: m.preview_image?.height ?? m.height ?? null,
        alt: alt && !UUID_ALT.test(alt) ? alt : product.title || '',
      };
    });
  }

  /** Live product data for the handles we resolved to. */
  async function fetchProducts(handles) {
    const results = await Promise.all(
      handles.map((h) =>
        fetch(`/products/${h}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );
    return results.filter(Boolean);
  }

  // ── the element ────────────────────────────────────────────────────────────
  class AirfluxFitment extends HTMLElement {
    connectedCallback() {
      this.scope = this.getAttribute('scope') || 'all';
      // 'hero' is the landing-page mini-PDP (§5.4): the page already looks like
      // a product page, and resolving swaps the image and price in place rather
      // than appending a card underneath.
      this.layout = this.getAttribute('layout') || 'inline';
      this.indexUrl = this.getAttribute('index-url');
      this.plateEndpoint = (this.getAttribute('plate-endpoint') || '').trim();
      // The RRP uplift, mirrored from the theme setting so the browser-painted
      // price can show the same comparison the server-rendered one does. 0, and
      // any value that fails to parse, means "no reference price" — which is
      // both the default and the only state that needs no justification.
      this.rrpPercent = Math.max(0, parseInt(this.getAttribute('rrp-percent'), 10) || 0);
      // The dispatch promise, rendered by `dispatchRow()` beside the stock line
      // rather than by the theme above it — see the note on the element in
      // `airflux-pdp-hero.liquid` for why it cannot live in the markup. A blank
      // cutoff means the merchant turned the countdown off, and nothing renders.
      this.dispatch = {
        cutoff: (this.getAttribute('dispatch-cutoff') || '').trim(),
        days: this.getAttribute('dispatch-days') || '',
        fallback: this.getAttribute('dispatch-fallback') || '',
      };
      // The guarantee line under Add to basket. Comes from the theme so this
      // file holds no second copy of a string the product page also prints —
      // blank simply renders nothing.
      this.assurance = (this.getAttribute('assurance') || '').trim();
      this.els = {
        make: this.querySelector('[data-airflux-make]'),
        model: this.querySelector('[data-airflux-model]'),
        years: this.querySelector('[data-airflux-years]'),
        submit: this.querySelector('[data-airflux-submit]'),
        result: this.querySelector('[data-airflux-result]'),
        status: this.querySelector('[data-airflux-status]'),
        hero: this.querySelector('[data-airflux-hero]'),
        // The gallery, on the two heroes that have one. `[data-airflux-hero]`
        // above is the FIRST slide inside it and still matters — it is what the
        // pre-paint pass paints the last car's photograph onto — but the slide
        // set as a whole belongs to the element, which rebuilds it on request.
        // `sections/airflux-landing.liquid` has the slot and no gallery, and is
        // why the single-image path below still exists.
        gallery: this.querySelector('airflux-gallery'),
        price: this.querySelector('[data-airflux-price]'),
        cascade: this.querySelector('[data-airflux-cascade]'),
        reg: this.querySelector('[data-airflux-reg]'),
        plateSubmit: this.querySelector('[data-airflux-plate-submit]'),
        plateMsg: this.querySelector('[data-airflux-plate-msg]'),
        // The landing hero's two extra slots: the car in the <h1>'s quiet half,
        // and the fit line that sits above the price the way it does on a real
        // product page. Absent on the standalone panel, which has neither.
        fitline: this.querySelector('[data-airflux-fitline]'),
        titleCar: this.querySelector('[data-airflux-title-car]'),
        // Two more slots that live OUTSIDE `[data-airflux-result]`, because the
        // result is rebuilt wholesale on every chip click and these two belong
        // to rows the markup already owns: the exits row at the foot of the
        // offer, and the first accordion. Absent on the standalone panel, which
        // has neither — everything below falls back to the old inline form.
        xsell: this.querySelector('[data-airflux-xsell-slot]'),
        options: this.querySelector('[data-airflux-options]'),
        details: this.querySelector('[data-airflux-details]'),
      };

      // Does the PAGE already own a "not your car?" control?
      //
      // The landing hero carries one in its exits row; the standalone closing-CTA
      // panel does not. Where it exists, the gap states must not paint a second
      // one — a car we stock nothing for was showing "Not your car?" from the
      // gap markup and "Not your car? Change it" from the theme, stacked, two
      // buttons firing the identical `data-airflux-change` handler.
      //
      // Read at connect, while the result is still empty, and rejected if it
      // somehow sits inside the result — that would be one the engine painted,
      // and it disappears on the next repaint.
      const ownChange = this.querySelector('[data-airflux-change]');
      this.els.change = ownChange && !ownChange.closest('[data-airflux-result]') ? ownChange : null;
      // The merchant's own quiet half and generic first slide, kept so every
      // unresolved state can put them back. Without the slide, picking "Not
      // sure" on a car whose sets are all version-specific left the PREVIOUS
      // version's photograph in the gallery beside a column saying nothing here
      // fits — a picture of a part we had just declined to sell them.
      this._titleCarDefault = this.els.titleCar?.textContent ?? '';
      // Where there is a gallery it keeps its own generic slides and puts them
      // back on `setMedia(null)`, modifier and all — these two are only for the
      // galleryless landing section.
      this._heroDefault = this.els.hero?.innerHTML ?? null;
      // A cut-out has to be shown whole, so the stand-in's `--bleed` comes off
      // when a catalogue shot arrives. Putting the stand-in back has to put the
      // modifier back with it, or it comes home letterboxed.
      this._heroDefaultBleed = this.els.hero?.classList.contains('af-gallery__figure--bleed') ?? false;
      // Buy boxes fetched from the Section Rendering API, keyed by handle. The
      // promise is cached rather than the text, so two chip clicks racing on the
      // same product share one request.
      this._buyBoxes = new Map();

      this.els.make?.addEventListener('change', () => this.onMake());
      this.els.model?.addEventListener('change', () => this.onModel());
      this.els.years?.addEventListener('change', () => this.onYears());
      this.els.submit?.addEventListener('click', () => {
        this._userActed = true;
        this.resolve();
      });

      // Tabs (plate ↔ manual). Plate stays the default tab — the provider is
      // wired later and the widget already falls back cleanly without it (§3).
      for (const tab of this.querySelectorAll('[data-airflux-tab]')) {
        tab.addEventListener('click', () => this.pickTab(tab.dataset.airfluxTab));
        tab.addEventListener('keydown', (e) => this.onTabKey(e));
      }
      this.els.plateSubmit?.addEventListener('click', () => {
        this._userActed = true;
        this.lookupPlate();
      });
      this.els.reg?.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        this._userActed = true;
        this.lookupPlate();
      });
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-airflux-retry]')) this.boot();
        const add = e.target.closest('[data-airflux-add]');
        if (add) this.addToCart(add);

        // Scoped to the group the button belongs to. It used to take the first
        // `.airflux-helper` in the whole widget, which was right while there was
        // only ever one — with a Type row and a Set row on the same car, every
        // "what's the difference?" opened the same paragraph.
        const help = e.target.closest('[data-airflux-helper]');
        if (help) {
          const panel = help.closest('.af-opt')?.querySelector('.af-opt__help');
          if (panel) {
            panel.hidden = !panel.hidden;
            help.setAttribute('aria-expanded', String(!panel.hidden));
          }
        }

        // Version choice — a filter over the products already fetched, so it
        // repaints instantly rather than re-resolving the car. It is also
        // REMEMBERED (§5.5): the choice has to reach the product page, the cart
        // and the order, or the page the customer lands on can only confirm at
        // whole-car granularity and will over-claim.
        const ver = e.target.closest('[data-airflux-version]');
        if (ver) {
          this._version = ver.dataset.airfluxVersion || null;
          this._unsure = false;
          this.rememberVersion();
          track('version_selected', { vehicle: this._vehicle?.h, version: this._version });
          this.paintResult();
          this.writeCarUrl(this._vehicle);
        }
        if (e.target.closest('[data-airflux-unsure]')) {
          this._unsure = true;
          this._version = null;
          this.rememberVersion();
          track('version_unsure', { vehicle: this._vehicle?.h });
          this.paintResult();
          this.writeCarUrl(this._vehicle);
        }

        // A preference chip — in-channel vs out-channel, 2-piece vs 4-piece.
        // Unlike the version question above, both answers genuinely fit, so
        // this only changes WHICH of the resolved products is current. Nothing
        // is re-resolved and nothing is re-fetched.
        const chip = e.target.closest('[data-airflux-axis]');
        if (chip) {
          this.chooseAxis(chip.dataset.airfluxAxis, chip.dataset.airfluxAxisValue);
          track('channel_selected', { vehicle: this._vehicle?.h, axis: chip.dataset.airfluxAxis });
        }

        // "Tell us you want these" — the demand signal for the category we do
        // NOT stock for this car. It is the router's second row when there is
        // only one real one, so the panel answers at a constant height.
        const demand = e.target.closest('[data-airflux-demand]');
        if (demand) this.renderDemand(demand.dataset.airfluxDemand);

        // A router row whose destination is the page we are already on. The
        // stored car has already reached the hero above through
        // `airflux:car-changed`, so all that is left to do is go and look at it.
        if (e.target.closest('[data-airflux-scroll]')) this.scrollToSurface();

        // "Mine isn't listed here" — the honest exit from a car whose version
        // we can't describe. Records what they were looking at (§7.1).
        if (e.target.closest('[data-airflux-notmine]')) this.renderNotMine();
        // "Can't find your car?" — the same exit one level up, for a car the
        // cascade has no row for at all. It sits in the manual panel's markup
        // rather than in a result state, so it is reachable before anything has
        // been resolved, which is the only moment it is any use.
        if (e.target.closest('[data-airflux-nocar]')) this.renderNoCar();
        // …and the way back. This button rendered with `data-airflux-body`, a
        // leftover from the body→version rename that nothing listened for, so
        // "Mine isn't listed here" was a one-way door out of the results.
        if (e.target.closest('[data-airflux-back]')) this.paintResult();
        // "Not your car?" — bring the question back without losing the page.
        if (e.target.closest('[data-airflux-change]')) this.showControls();

        // Plate confirmation (§3 outcome 1).
        if (e.target.closest('[data-airflux-confirm-yes]') && this._pendingCar) {
          const { v, year } = this._pendingCar;
          this.setCar({
            handle: v.h,
            year: year || null,
            make: this.index.makes[v.m],
            model: v.n,
            body: v.b || null,
            // The generation range as the row states it. Kept alongside the
            // exact year so the order can carry both facts without conflating
            // them (see assets/airflux-order.js).
            years: `${v.f}–${v.t}`,
            label: `${this.label(v)}${year ? ` ${year}` : ''}`,
          });
          track('fit_confirmed', { vehicle: v.h, via: 'plate', year });
          this._pendingCar = null;
          this.render(v);
        }
        // The family question, answered. The member they picked IS the
        // confirmation, so it goes straight to the answer — a second "is this
        // your car?" would ask what they just said. A member this page does
        // not stock for (the coupé, on deflectors) lands on the scope gap,
        // which names the car and offers the line we do have.
        const member = e.target.closest('[data-airflux-family]');
        if (member && this._pendingFamily) {
          const { year } = this._pendingFamily;
          const v = this.index.vehicles.find((x) => x.h === member.dataset.airfluxFamily);
          this._pendingFamily = null;
          if (!v) return;
          this.setCar({
            handle: v.h,
            year: year || null,
            make: this.index.makes[v.m],
            model: v.n,
            body: v.b || null,
            years: `${v.f}–${v.t}`,
            label: `${this.label(v)}${year ? ` ${year}` : ''}`,
          });
          track('fit_confirmed', { vehicle: v.h, via: 'plate_family', year });
          if (this.inScope(v)) {
            this.render(v);
          } else {
            // `render()` is what normally resets the version and rewrites the
            // address bar for a new car; the scope gap does neither, so a
            // `?car=` left by an earlier visit would have named the OTHER car
            // and brought it back on reload. Same two lines, here.
            this._version = null;
            this._unsure = false;
            this.writeCarUrl(v);
            this.renderScopeGap(v);
          }
        }
        if (e.target.closest('[data-airflux-confirm-no]')) {
          this._pendingCar = null;
          this._pendingFamily = null;
          this.showControls();
          this.switchTab('manual');
        }
      });

      // Another widget (or the sticky bar) changed the car — re-resolve so
      // every placement on the page agrees.
      document.addEventListener('airflux:car-changed', (e) => {
        if (!this.index || !e.detail?.handle) return;

        // 🔴 …but never re-resolve our OWN echo. `Car.set` fires this event and
        // this widget calls `Car.set` — from `resolve`, from the plate
        // confirmation, from a `?car=` link and from every version choice — so
        // each of those ran `render()` a second time on top of itself. Two
        // costs, and the second one is a real defect: a wasted round trip to
        // `/products/{h}.js` every time, and a repaint that replaced the node
        // `focusAnswer` had just focused, dropping a keyboard user to <body>.
        //
        // A flag rather than a comparison of the detail, because `Car.set` runs
        // BEFORE `render()` — at the moment the event fires this widget is
        // still showing the previous car, so nothing in the payload can tell
        // its own write apart from another placement's.
        if (this._writingCar) return;

        const v = this.index.vehicles.find((x) => x.h === e.detail.handle);
        if (!v) return;
        // Out of this placement's scope is still an answer — say what we do
        // have rather than leaving the previous car's result on screen.
        if (this.inScope(v)) this.render(v);
        else this.renderScopeGap(v);
      });

      this.boot();
    }

    async boot() {
      this.setStatus('Loading vehicles…');
      try {
        this.index = await loadIndex(this.indexUrl);
      } catch {
        // Compact retry state INSIDE the widget; the rest of the page is fine.
        this.setStatus('');
        this.settle();
        this.releaseReservation();
        this.els.result.innerHTML = this.withXsell(
          '<div class="af-gap"><p>Couldn\'t load the vehicle list.</p>' +
          '<button type="button" class="af-gap__btn" data-airflux-retry>Try again</button></div>');
        return;
      }
      this.setStatus('');
      this.buildMakes();
      this.showFromPrice();
      // `?car=` outranks the stored car: a link somebody followed is a more
      // deliberate statement than something this browser remembered, and it is
      // what makes a resolved page shareable and usable as an ad destination.
      if (!this.applyCarFromUrl()) this.restore();
      this.applyRegFromUrl();
    }

    /**
     * `?car=<vehicle-handle>&v=<version>` — a page addressed to one car.
     *
     * This is how the router hands a customer from the home page to the landing
     * page that sells for their car, and it is deliberately a plain URL rather
     * than a private channel: the result is then shareable, survives the back
     * button, and can be the destination of an ad or an email.
     *
     * Only the first finder on the page acts on it, for the same reason
     * `applyRegFromUrl` does — two widgets both writing the car would fire the
     * change event twice and re-render everything on the page for no gain.
     */
    applyCarFromUrl() {
      if (document.querySelector('airflux-fitment') !== this) return false;
      const params = new URLSearchParams(window.location.search);
      const handle = params.get('car');
      if (!handle) return false;

      const v = this.index.vehicles.find((x) => x.h === handle);
      if (!v) return false;

      // 🔴 Checked against the car's own options before it is trusted. The page
      // already IGNORED an unknown version for display — `render` only restores
      // one that still exists — but it was still written to storage, and from
      // there `cartAttributes` put it on the order: a hand-edited `?v=Banana`
      // reached the packing slip as "Audi A3 · 2004–2012 · Banana". Nothing a
      // customer would do, and everything the order data must not carry.
      const claimed = params.get('v');
      const version = claimed && (v.vo ?? []).some((o) => o.l === claimed) ? claimed : null;
      const car = Car.get();

      // The version this page is about: the URL's if it named a real one, and
      // otherwise whatever this browser already had for the same car.
      const resolved = version || (car?.handle === handle ? car.version : null) || null;

      // 🔴 Its code and its body travel WITH it, and are LOOKED UP rather than
      // inherited.
      //
      // The spread below carries the whole stored car forward, which is what
      // keeps a plate's year — and it was carrying `versionCode` and
      // `versionBody` too, while only `version` was overwritten. The three
      // could therefore disagree, and the two that go stale are the two the
      // packing slip reads (`carProperties` takes `Body` from `versionBody`
      // and `Generation` from `versionCode`, never from `version`):
      //
      //   · A fresh browser opening a shared `?v=` link had never written
      //     either, so the line item went out with NO body and NO generation
      //     while the URL above it named one.
      //   · A browser that had picked a version by chip and then arrived on a
      //     link naming a different one kept the FIRST body on the line — a
      //     slip reading "5-door Estate" under a car resolved as the Hatchback.
      //
      // Only `rememberVersion` else writes these, from this same `vo` list by
      // this same expression, so re-deriving can never lose a fact another
      // path knew. A version the index no longer lists takes its body down
      // with it, which is the honest end for a claim nothing can confirm.
      const opt = (v.vo ?? []).find((o) => o.l === resolved) ?? null;
      const versionCode = opt?.c ?? null;
      const versionBody = opt?.b ?? null;

      // `Car.set` mirrors the car onto the cart, which is a network write. A
      // link followed twice, or a refresh, says nothing new — so only write
      // when the URL actually disagrees with what is already stored.
      //
      // The derived pair is part of "disagrees": a browser carrying the stale
      // shape above repairs itself on its next visit and then falls silent
      // again, rather than buying with a wrong body for as long as the link
      // stays bookmarked.
      const unchanged =
        car?.handle === handle &&
        (car.version ?? null) === resolved &&
        (car.versionCode ?? null) === versionCode &&
        (car.versionBody ?? null) === versionBody;
      if (unchanged) {
        if (!this.inScope(v)) this.renderScopeGap(v);
        else this.render(v);
        return true;
      }

      this.setCar({
        // A car already stored for this same vehicle keeps its year: the plate
        // lookup is the only thing that ever knew one, and the URL cannot carry
        // a fact we would otherwise have to invent.
        ...(car?.handle === handle ? car : {}),
        handle: v.h,
        make: this.index.makes[v.m],
        model: v.n,
        body: v.b || null,
        years: `${v.f}–${v.t}`,
        version: resolved,
        versionCode,
        versionBody,
        label: this.label(v),
      });

      if (!this.inScope(v)) {
        this.renderScopeGap(v);
        return true;
      }
      this.render(v);
      return true;
    }

    /**
     * Writes the resolved car into the address bar, without a history entry.
     *
     * `replaceState` rather than `pushState`: resolving is not navigation, and
     * a back button that steps through four chip clicks before leaving the page
     * is a worse back button. The landing page is reached by a real link from
     * the router, which is where the history entry belongs.
     */
    writeCarUrl(v) {
      if (this.layout !== 'hero' || !v) return;
      const url = new URL(window.location.href);
      url.searchParams.set('car', v.h);
      if (this._version) url.searchParams.set('v', this._version);
      else url.searchParams.delete('v');
      // A reg that has already been spent is not worth carrying forward — it
      // would re-run the lookup, and bill the provider, on every refresh.
      url.searchParams.delete('reg');
      window.history.replaceState(window.history.state, '', url);
    }

    /**
     * `?reg=` — a registration typed somewhere that could not resolve it on the
     * spot. Today that is the header's plate entry on a page carrying no finder
     * of its own: rather than inventing a channel for it, the band submits its
     * form to the home page and the reg arrives as an ordinary query string.
     *
     * The URL being the state is what makes the result shareable and the back
     * button behave. It beats a stored car, because typing a reg is the more
     * deliberate of the two acts.
     *
     * Only the first finder on the page acts on it. Two widgets each looking
     * the same reg up would bill the provider twice for one answer.
     */
    applyRegFromUrl() {
      if (!this.els.reg || document.querySelector('airflux-fitment') !== this) return;

      const reg = new URLSearchParams(window.location.search).get('reg');
      if (!reg) return;

      this.els.reg.value = reg.toUpperCase();
      this.switchTab('plate');
      this.scrollIntoView({
        block: 'center',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
      this.lookupPlate();
    }

    /**
     * "From £XX" in the generic state (§5.4) — the cheapest product of this
     * page's type. Computed from the index, so it costs no extra request and
     * can never disagree with what the widget will resolve to.
     */
    showFromPrice() {
      if (!this.els.price || this.scope === 'all') return;
      const wanted = this.scope === 'wipers' ? 'w' : 'd';
      const ofType = this.index.products.filter((p) => p.t === wanted);
      // In stock only. "From £14.90" quoting a sold-out line is a price nobody
      // can pay, and the customer meets the real one two clicks later.
      const sellable = ofType.filter((p) => p.a);
      const prices = (sellable.length ? sellable : ofType).map((p) => p.p);
      if (!prices.length) return;
      // No reference price against a FROM price. "From £29.90, was £37.90" is
      // two ranges pretending to be one comparison, and the cheapest line is
      // rarely the one the customer ends up on anyway.
      this.els.price.innerHTML = `<p class="af-price__now">From ${escapeHtml(
        money.format(Math.min(...prices) / 100)
      )}</p>`;
    }

    /**
     * The price, as the three figures the product page server-renders.
     *
     * 🔴 Keep in step with `snippets/airflux-price-block.liquid` — same classes,
     * same rounding, same rule about when the accent is allowed. Two
     * implementations exist because a landing page has no product until a car
     * resolves, and one of them has to run in the browser.
     *
     * Takes a real `compare_at_price` when the caller has one and falls back to
     * `rrp-percent` when it does not — the same order as `airflux-rrp.liquid`.
     *
     * 🔴 Only ONE caller can supply it, and that is not an oversight. The buy
     * column has already fetched `/products/{handle}.js` for the car it
     * resolved, so it holds the real variant. Every other price on these pages
     * is a "from £x" across several candidates, drawn from the fitment index,
     * which carries a price per product and nothing else — a reference price
     * there would cost a round trip per candidate.
     *
     * Found 2026-09-02, after real compare-at prices were written onto the
     * catalogue and the RRP percentage was switched off: `/products/<handle>`
     * server-rendered "£48.66 → £39.90" while the resolved card on the landing
     * page showed £39.90 alone. Same product, two prices, decided by the route
     * in. Passing what the caller already has closes it.
     *
     * ⚠️ `> pence` rather than merely present: 656 products arrived from
     * LitCommerce carrying `compare_at_price` of £0.00, which is not a price.
     * The Liquid guards on the same condition, for the same reason.
     */
    priceMarkup(pence, compareAt) {
      const now = money.format(pence / 100);
      const real = Number(compareAt);
      if (Number.isFinite(real) && real > pence) {
        const save = Math.round(((real - pence) / real) * 100);
        return (
          `<p class="af-price__was"><span class="af-visually-hidden">Was </span>` +
          `<s>${escapeHtml(money.format(real / 100))}</s></p>` +
          `<p class="af-price__now">${escapeHtml(now)}</p>` +
          `<p class="af-price__save">Save ${save}%</p>`
        );
      }
      const pct = this.rrpPercent;
      if (!pct) return `<p class="af-price__now">${escapeHtml(now)}</p>`;

      // Uplift → nearest whole pound → 10p off, the £x9.90 shape. Same three
      // steps as the Liquid, and for the same reason: a reference price with
      // arithmetic pence on the end reads as a calculation.
      const was = Math.round((pence * (100 + pct)) / 100 / 100) * 100 - 10;
      if (was <= pence) return `<p class="af-price__now">${escapeHtml(now)}</p>`;

      // Reference price first, so the reading order matches the reading order —
      // see the note in the Liquid snippet.
      const save = Math.round(((was - pence) / was) * 100);
      return (
        `<p class="af-price__was"><span class="af-visually-hidden">Was </span>` +
        `<s>${escapeHtml(money.format(was / 100))}</s></p>` +
        `<p class="af-price__now">${escapeHtml(now)}</p>` +
        `<p class="af-price__save">Save ${save}%</p>`
      );
    }

    setStatus(text) {
      if (this.els.status) this.els.status.textContent = text;
    }

    /**
     * Stores the car, marking the change as ours.
     *
     * `Car.set` dispatches `airflux:car-changed` synchronously, and this element
     * listens for it so that a car chosen in one placement reaches the others.
     * Every write therefore came back as an instruction to re-resolve what we
     * were already resolving. The flag is only raised for the duration of the
     * dispatch, so another placement's write still gets through.
     */
    setCar(car) {
      this._writingCar = true;
      try {
        Car.set(car);
      } finally {
        this._writingCar = false;
      }
    }

    /**
     * The question stands down so the answer can take its place.
     *
     * 🔴 On the standalone panel this is what stops the hero stretching, and it
     * does it structurally rather than by making the answer smaller. The panel
     * floats on a photograph inside a composition of fixed proportions, so an
     * answer APPENDED under the field grows the hero by however tall the answer
     * happens to be — which is unbounded, because a car name and two product
     * rows are not a fixed size. Replacing the field instead makes the two
     * states the same thing at two different sizes, and pinning the measured
     * height of the question makes them the same size as well.
     *
     * Measured rather than declared, because the height depends on copy the
     * merchant edits: the reassurance line under the plate is a theme setting,
     * and a hard-coded `min-block-size` would be wrong the day it changes.
     */
    hideControls() {
      const cascade = this.els.cascade;
      if (!cascade || cascade.hasAttribute('hidden')) return;
      cascade.setAttribute('hidden', '');
      this.els.submit?.setAttribute('hidden', '');
    }

    /** "Not your car?" — the question comes back, the page keeps its place. */
    showControls() {
      this.els.cascade?.removeAttribute('hidden');
      this.els.submit?.removeAttribute('hidden');
      this.settle();
      this.releaseReservation();

      if (this.els.result) this.els.result.innerHTML = this.withXsell( '');
      this.paintFitLine(null);
      this.paintTitleCar(null);

      // The stored car is deliberately NOT cleared: the customer said this page
      // is not about their car, which is not the same as saying we have the car
      // wrong. Clearing it would empty the header's own "Your car" band and the
      // cart attribute with it.
      this._vehicle = null;
      this._shown = null;
      this._current = null;
      this._visibleForAxes = null;
      this._focusedFor = null;
      this.paintHero(null);
      this.paintDetails(null);
      this.showFromPrice();
      this.announce(false);

      // The address stops claiming a car this page is no longer showing, so a
      // refresh does not resolve straight back into the state they just left.
      if (this.layout === 'hero') {
        const url = new URL(window.location.href);
        url.searchParams.delete('car');
        url.searchParams.delete('v');
        window.history.replaceState(window.history.state, '', url);
      }

      this.els.cascade?.scrollIntoView({ block: 'nearest', behavior: reduceMotion() ? 'instant' : 'smooth' });
      // Focus follows the question, or a keyboard user is left on a button that
      // no longer exists and has to hunt for where the page went. It goes to
      // whichever panel is actually showing: focusing the plate field while the
      // make/model tab is open focuses a control inside `[hidden]`, which does
      // nothing at all and silently leaves focus on <body>.
      const panel = [...this.querySelectorAll('[data-airflux-panel]')].find((p) => !p.hidden);
      this.moveFocusTo(panel?.querySelector('input, select, button') ?? this.els.reg);
    }

    /** Take them to the buy column this page has just resolved. */
    scrollToSurface() {
      const target = document.querySelector('[data-airflux-surface]') ?? document.querySelector('airflux-fitment');
      target?.scrollIntoView({ block: 'start', behavior: reduceMotion() ? 'instant' : 'smooth' });
    }

    /** Vehicles that carry a product of the type this placement sells. */
    inScope(v) {
      if (this.scope === 'deflectors') return !!v.d?.length;
      if (this.scope === 'wipers') return !!v.w?.length;
      return !!(v.d?.length || v.w?.length);
    }

    productsFor(v) {
      const ids = this.scope === 'wipers' ? v.w ?? [] : this.scope === 'deflectors' ? v.d ?? [] : [...(v.d ?? []), ...(v.w ?? [])];
      return ids.map((i) => this.index.products[i]).filter(Boolean);
    }

    label(v) {
      return `${this.index.makes[v.m]} ${v.n}${v.b ? ` ${v.b}` : ''}`;
    }

    /**
     * The car as the customer described it. A year appears only when we were
     * actually told one — the plate lookup supplies it; the manual cascade
     * doesn't, and guessing would be exactly the kind of invented fact the
     * whole spec guards against.
     */
    carLabel(v) {
      const car = Car.get();
      const year = car?.handle === v.h && car.year ? ` ${car.year}` : '';
      return `${this.label(v)}${year}`;
    }

    /** The car and the years — for lines that NAME the car rather than claim. */
    carLabelWithYears(v) {
      return `${this.label(v)} ${v.f}–${v.t}`;
    }

    /**
     * The car, always with a year — the exact one where the plate gave us it,
     * the picked generation's range otherwise.
     *
     * 🔴 This is what the fit line and the out-of-stock heading name the car
     * with, and it is the landing page's half of a change made on both heroes
     * on 2026-08-16 (see `carName()` in `airflux-pdp.js`, which does the same
     * job from browser storage). Without a year, "Fits your Ford Kuga" restated
     * an `<h1>` two lines below it that already read "Wiper blades, for your
     * Ford Kuga" — the sentence cost 45px and told the reader nothing they had
     * not just read. The year is the fact they actually chose.
     *
     * ⚠️ The range is the vehicle row's, the union across every listing for
     * that car, so it can run a year wider than one kit's own title (§5.3). It
     * names the CAR the customer picked, which is the row they selected in the
     * Years dropdown; whether this product fits it is still decided upstream,
     * and a car we cannot confirm never reaches this line at all.
     */
    carLabelDated(v) {
      const car = Car.get();
      if (car?.handle === v.h && car.year) return `${this.label(v)} ${car.year}`;
      return this.carLabelWithYears(v);
    }

    /**
     * The exact string the customer picked in the Years dropdown.
     *
     * 🔴 Used wherever we claim something fits "any" of this car, because the
     * row is narrower than its years. "Fits any BMW 1 Series 2011–2019" reads
     * as every 1 Series built in those eight years — but the row covers the F20
     * and the F21, and if BMW had also made an F22 in that window we would be
     * claiming a car we have never heard of. Mirroring the dropdown verbatim
     * makes the scope of the promise exactly the scope of the selection:
     * "Fits any BMW 1 Series 2011–2019 · F20 F21".
     */
    rowLabel(v) {
      return [`${v.f}–${v.t}`, v.g, v.b].filter(Boolean).join(' · ');
    }

    buildMakes() {
      this.scoped = this.index.vehicles.filter((v) => this.inScope(v));
      const makes = [...new Set(this.scoped.map((v) => v.m))].sort((a, b) =>
        this.index.makes[a].localeCompare(this.index.makes[b])
      );
      this.fill(this.els.make, makes.map((m) => ({ value: m, label: this.index.makes[m] })), 'Select make');
      this.reset(this.els.model, 'Select model');
      this.reset(this.els.years, 'Select year');
      this.setSubmit(false);
    }

    onMake() {
      const m = this.els.make.value;
      this.reset(this.els.years, 'Select year');
      this.setSubmit(false);
      if (m === '') return this.reset(this.els.model, 'Select model');

      const models = [...new Set(this.scoped.filter((v) => String(v.m) === m).map((v) => v.n))].sort((a, b) =>
        a.localeCompare(b)
      );
      this.fill(this.els.model, models.map((n) => ({ value: n, label: n })), 'Select model');
    }

    onModel() {
      const m = this.els.make.value;
      const n = this.els.model.value;
      this.setSubmit(false);
      if (n === '') return this.reset(this.els.years, 'Select year');

      // One option per generation; body style disambiguates same-year variants
      // (a 3-door and a 5-door take different kits).
      const matches = this.scoped
        .filter((v) => String(v.m) === m && v.n === n)
        .sort((a, b) => a.f - b.f);

      /**
       * One option per generation, labelled the way an owner would recognise it.
       *
       * Per-year selection was tried and reverted: it turned a median of ONE
       * option into a median of nine, to fix a padded end year on exactly one
       * vehicle in the catalogue. Generation selection is lower friction and
       * usually needs no choice at all.
       *
       * The range shown is exactly what the listings state — no padding for
       * "onwards" titles, so there is never a year here that nobody claimed.
       *
       * The chassis codes carry the real weight here: "2011–2019 · F20 F21"
       * is how an owner recognises the row as their car, and it is what tells
       * an F21 (3-door) owner that this generation includes them. Body appears
       * only when EVERY product for the car agrees on one — otherwise it is a
       * property of the individual kit and belongs on the cards below.
       */
      this.fill(
        this.els.years,
        matches.map((v) => ({
          value: v.h,
          label: [`${v.f}–${v.t}`, v.g, v.b].filter(Boolean).join(' · '),
        })),
        'Select year'
      );
      // Most models have exactly one generation — preselect it so the customer
      // isn't asked a question with a single possible answer.
      if (matches.length === 1) {
        this.els.years.value = matches[0].h;
        this.setSubmit(true);
      }
    }

    onYears() {
      this.setSubmit(this.els.years.value !== '');
    }

    fill(select, options, placeholder) {
      if (!select) return;
      select.innerHTML =
        `<option value="">${placeholder}</option>` +
        options.map((o) => `<option value="${escapeAttr(String(o.value))}">${escapeHtml(o.label)}</option>`).join('');
      select.disabled = options.length === 0;
    }

    reset(select, placeholder) {
      if (!select) return;
      select.innerHTML = `<option value="">${placeholder}</option>`;
      select.disabled = true;
    }

    setSubmit(enabled) {
      if (this.els.submit) this.els.submit.disabled = !enabled;
    }

    restore() {
      const car = Car.get();
      if (!car?.handle) return;
      const v = this.index.vehicles.find((x) => x.h === car.handle);

      // The car is known but this page doesn't sell for it. Measured on the
      // live index: 476 of 539 cars are stocked for only ONE of the two
      // categories, so this is the common case, not an edge one — and it used
      // to `return` in silence, leaving an untouched cascade while the sticky
      // bar still said "Your car: …". §5.5's context-aware gap message existed
      // but was only ever reachable from the plate tab.
      if (v && !this.inScope(v)) {
        this.renderScopeGap(v);
        return;
      }
      if (!v) {
        // A stored handle the index no longer has — the car was re-keyed or
        // its products went away. Say so rather than pretending nothing is set.
        this.setStatus("We've updated our listings — please pick your car again.");
        Car.clear();
        return;
      }

      this.els.make.value = String(v.m);
      this.onMake();
      this.els.model.value = v.n;
      this.onModel();
      this.els.years.value = v.h;
      this.setSubmit(true);
      this.render(v);
    }

    // ── tabs ──────────────────────────────────────────────────────────────
    /**
     * A tab the CUSTOMER chose, as opposed to one the flow moved for them.
     *
     * 🔴 The difference is the status line. A failed lookup switches to the
     * cascade and explains itself — "pick your car below" — and that sentence
     * then survived a tap back onto the Plate tab, where it sat above a reg
     * field telling somebody to pick their car below instead. It is an answer
     * about a lookup they have just walked away from.
     *
     * Clearing it inside `switchTab` would not work: the failure path switches
     * FIRST and writes the message second, so the message would clear itself.
     * Clearing it here, at the two places a person can choose a tab, leaves
     * every programmatic switch — the failure path, the confirm-no path, the
     * `?reg=` deep link — to say whatever it needs to.
     */
    pickTab(which, opts) {
      this.setStatus('');
      this.switchTab(which, opts);
    }

    switchTab(which, { focusTab = false } = {}) {
      for (const tab of this.querySelectorAll('[data-airflux-tab]')) {
        const on = tab.dataset.airfluxTab === which;
        tab.setAttribute('aria-selected', String(on));
        // Roving tabindex: only the selected tab is in the page's tab order,
        // and the arrow keys move between them (WAI-ARIA tabs pattern).
        tab.tabIndex = on ? 0 : -1;
        if (on && focusTab) tab.focus();
      }
      for (const panel of this.querySelectorAll('[data-airflux-panel]'))
        panel.hidden = panel.dataset.airfluxPanel !== which;
      if (which === 'plate' && !focusTab) this.els.reg?.focus();
    }

    /** ← → Home End across the tablist, as the tabs pattern requires. */
    onTabKey(e) {
      const tabs = [...this.querySelectorAll('[data-airflux-tab]')];
      const i = tabs.indexOf(e.target);
      if (i < 0) return;
      const to =
        e.key === 'ArrowRight' ? (i + 1) % tabs.length
        : e.key === 'ArrowLeft' ? (i - 1 + tabs.length) % tabs.length
        : e.key === 'Home' ? 0
        : e.key === 'End' ? tabs.length - 1
        : -1;
      if (to < 0) return;
      e.preventDefault();
      this.pickTab(tabs[to].dataset.airfluxTab, { focusTab: true });
    }

    // ── plate lookup — the three outcomes (§3) ───────────────────────────────
    async lookupPlate() {
      const reg = (this.els.reg?.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (!reg) return;
      track('plate_lookup_attempt', {});

      this.setPlateMsg('Looking up…');
      this.els.plateSubmit.disabled = true;

      let data = { status: 'lookup_unavailable' };
      if (this.plateEndpoint) {
        try {
          // ~5s client timeout → treat as unavailable and fall back (§4).
          const ctrl = new AbortController();
          const timer = setTimeout(() => ctrl.abort(), 5000);
          const res = await fetch(`${this.plateEndpoint}?reg=${encodeURIComponent(reg)}`, { signal: ctrl.signal });
          clearTimeout(timer);
          // A 429 carries a MEANINGFUL status in its body — `rate_limited` or
          // `daily_limited` — and `res.ok` alone would throw it away, leaving the
          // generic "not right now" message on a case we can explain properly.
          // Every other non-2xx (including a 403 from the Worker's origin lock)
          // deliberately keeps the default and falls back without comment.
          if (res.ok || res.status === 429) data = await res.json();
        } catch {
          data = { status: 'lookup_unavailable' };
        }
      }

      this.els.plateSubmit.disabled = false;

      // Outcome 3 — not identified / unavailable → manual tab, non-blaming (§3).
      if (data.status !== 'ok') {
        track('plate_lookup_fail', { status: data.status });
        this.setPlateMsg('');
        this.switchTab('manual');
        // 🔴 ONLY ONE OF THESE OUTCOMES IS THE CUSTOMER'S BUSINESS (2026-09-01).
        //
        // A reg we couldn't identify is a fact about THEIR CAR: they typed
        // something, we looked, it isn't there. Say so, or the tab silently
        // changes under them and they retype the same reg thinking it didn't
        // register.
        //
        // Everything else is a fact about US — their per-day allowance, the burst
        // limiter, the shop's daily ceiling, a DVSA outage, a timeout. None of it
        // means anything to someone buying wind deflectors, and naming it invites
        // exactly the wrong reaction: "no lookups left today" reads as the shop
        // being broken or rationed, when the manual picker sitting right there
        // works perfectly and sells the same part. So we say NOTHING and just
        // switch tabs. The picker appearing IS the instruction.
        //
        // This replaces the opposite rule, which named the limits so people would
        // stop retrying. The tab switch already stops them, and it does it without
        // telling a customer about our infrastructure.
        //
        // ⚠️ The one line that remains still has to fit ONE line at 375px — it
        // sits under the tabs, where a wrap pushes the whole cascade down. Budget
        // is 303px at 14px and this is 275px. Measured, not guessed: check any
        // replacement at 375 before shipping it, and prefer dropping words to
        // shrinking type, because it has to stay readable on glass. (It is also
        // written to fit a 320px screen, which leaves 248px.)
        this.setStatus(data.status === 'not_found' ? 'Reg not found — pick your car below.' : '');
        return;
      }

      track('plate_lookup_success', { make: data.make, model: data.model, year: data.year });
      this.setPlateMsg('');
      this.resolvePlate(data);
    }

    /**
     * Match an identified car to our index and route to outcome 1 or 2.
     *
     * 🔴 Rewritten 2026-07-31, with the first real provider. The old test was
     * `dvModel.includes(ourModel) || ourModel.includes(dvModel)` on
     * punctuation-stripped strings, taking the FIRST hit in index order.
     * Replaying all 534 catalogue models through it, **63 resolved to a
     * different car** — Audi S3 → RS 3, Ford Transit Connect → Transit, Citroën
     * C4 Cactus → C4, Dacia Sandero Stepway → Sandero, Mercedes CLA → A-Class.
     * Every one of those is a wrong-fitment claim about a real part, which is
     * what principle #2 exists to prevent. It was unreachable while the provider
     * was stubbed, so it ships fixed alongside the provider rather than after.
     *
     * Two changes: matching is token-wise and prefix-anchored (see
     * `modelMatchLen`), and among the candidates the MOST SPECIFIC wins instead
     * of whichever the index happened to list first.
     */
    resolvePlate(car) {
      const year = parseInt(car.year, 10);
      const dvMake = makeKey(car.make);

      // Every spelling the Worker accepted for this car, most specific first.
      // The catalogue is inconsistent about which it stores ("GLC-Class" but
      // bare "CLA"), so both are offered and a whole-name match against either
      // counts. Falls back to whatever the provider literally said when the
      // vocabulary recognised nothing.
      const names = [car.model, ...(car.modelAlts || [])].map(modelTokens).filter((t) => t.length);
      if (!names.length) names.push(modelTokens(car.modelRaw));

      // Where the model was RECOGNISED, only a whole-name match counts (see
      // modelMatchLen). Where it wasn't, a prefix is allowed so trim words on an
      // unknown string still find the car — the pre-provider behaviour, now
      // confined to the case that actually needs it.
      // `family` is a whole name too — "2 Series" must not prefix-match its way
      // onto anything; what it CAN stand for is settled separately below.
      const wholeOnly = car.modelMatch === 'exact' || car.modelMatch === 'normalised' || car.modelMatch === 'family';

      // Most tokens matched wins. "TIGUAN ALLSPACE" offers both "Tiguan" (1) and
      // "Tiguan Allspace" (2); they are different cars taking different parts,
      // so the longer match is the only correct answer — and picking it is what
      // the old `.find()`, which took whatever the index listed first, could not.
      const best = (pool) => {
        let hit = null;
        let hitLen = 0;
        for (const v of pool) {
          if (makeKey(this.index.makes[v.m]) !== dvMake) continue;
          if (year && !(year >= v.f && year <= v.t)) continue;
          for (const dv of names) {
            const len = modelMatchLen(v.n, dv);
            if (len < 0 || (wholeOnly && len !== dv.length)) continue;
            if (len > hitLen) {
              hit = v;
              hitLen = len;
            }
          }
        }
        return hit;
      };

      // 🔴 A FAMILY is not a car — ask which member (2026-09-04).
      //
      // The Worker marks a model it INFERRED from an engine designation as
      // `family`: "218I M SPORT" says 2 Series, and BMW sells the F22 coupé,
      // the F45 Active Tourer and the F46 Gran Tourer under that one name, in
      // the same years, taking different parts. The register writes the same
      // string for all three (not one of 144,622 names the body), so no matcher
      // can be right here — and `best()` was silently taking the coupé, which
      // told an Active Tourer owner on the deflectors page that we make
      // nothing for their car while the F45 kit sat in stock, and on the wipers
      // page confirmed them onto the coupé's blade. Where the catalogue holds
      // more than one row under the family name, the honest answer is a
      // question — the one the version chooser already asks one level down.
      //
      // Asked BEFORE scoping, and every member is offered whether or not this
      // page stocks it: hiding the coupé because it has no deflectors would
      // walk its owner onto the Tourer's kit. A member out of scope lands on
      // the scope gap, which offers what we do have for it.
      if (car.modelMatch === 'family') {
        const { rows, exact } = this.familyRows(dvMake, names, year);
        if (rows.length > 1 || (rows.length === 1 && !exact)) {
          this.askFamily(rows, year, car);
          return;
        }
      }

      const inScopeMatch = best(this.index.vehicles.filter((v) => this.inScope(v)));
      if (inScopeMatch) {
        // Outcome 1 — found & covered. The plate gives a real YEAR, so we can
        // name it exactly (§5.4).
        this.confirmPlateCar(inScopeMatch, year, car);
        return;
      }

      // Identified, but not stocked for this page's category (or at all).
      this.renderCoverageGap(car, best(this.index.vehicles));
    }

    confirmPlateCar(v, year, car) {
      const label = `${this.label(v)}${year ? ` ${year}` : ''}`;
      // Shown as the REGISTER wrote it, not as we normalised it — that is the
      // wording on the customer's V5C, so it is what they can actually check.
      const shown = plateCarLabel(car);
      // Every state after a lookup REPLACES the field rather than stacking under
      // it. "No, choose manually" puts it straight back.
      this.hideControls();
      this.els.result.innerHTML = this.withXsell(
        `<div class="af-confirm">` +
        `<p class="af-confirm__q">✓ <strong>${escapeHtml(shown)}</strong>${year ? ` · ${year}` : ''} — is this your car?</p>` +
        `<div class="af-confirm__actions">` +
        `<button type="button" class="af-confirm__yes" data-airflux-confirm-yes>Yes, that's my car</button>` +
        `<button type="button" class="af-confirm__no" data-airflux-confirm-no>No, choose manually</button>` +
        `</div></div>`);
      this.moveFocusTo(this.els.result.firstElementChild);
      this._pendingCar = { v, year, label };
    }

    /**
     * Every catalogue row a family name could mean, for this make and year:
     * the row named exactly that, and every row whose name extends it —
     * "2 Series" → "2 Series", "2 Series Active Tourer", "2 Series Gran Tourer".
     * The exact name leads; the extensions follow in catalogue order.
     *
     * @returns {{ rows: object[], exact: object|null }}
     */
    familyRows(dvMake, names, year) {
      const ours = (v) => modelTokens(v.n);
      // `modelMatchLen(a, b)` asks whether a's tokens head b's — so the family
      // name goes first and the row's tokens second: does "2 Series" head
      // "2 Series Active Tourer"? It does, and it heads itself.
      const heads = (v) => names.some((dv) => modelMatchLen(dv.join(' '), ours(v)) === dv.length);
      const isExact = (v) => names.some((dv) => ours(v).length === dv.length && modelMatchLen(v.n, dv) === dv.length);
      const rows = this.index.vehicles.filter(
        (v) => makeKey(this.index.makes[v.m]) === dvMake && (!year || (year >= v.f && year <= v.t)) && heads(v)
      );
      const exact = rows.find(isExact) ?? null;
      return { rows: exact ? [exact, ...rows.filter((v) => v !== exact)] : rows, exact };
    }

    /**
     * The family question — "which 2 Series?" — in the plate confirmation's
     * own clothes, so a lookup that needs one more answer still reads as a
     * lookup. The chips are the version chooser's: a model name, and its
     * chassis codes only where they set it apart from the other options
     * ("F22 F23" says coupé; the two Tourers share "F45 F46", which would say
     * nothing). "None of these" is the manual picker — the same exit as
     * "No, choose manually", which is what its attribute is.
     */
    askFamily(rows, year, car) {
      const shown = plateCarLabel(car);
      const sharing = new Map();
      for (const v of rows) if (v.g) sharing.set(v.g, (sharing.get(v.g) ?? 0) + 1);
      const chip = (v) =>
        `<button type="button" class="af-chip" data-airflux-family="${escapeAttr(v.h)}">` +
        escapeHtml(v.n) +
        (v.g && sharing.get(v.g) === 1 ? ` <span class="af-chip__code">${escapeHtml(v.g)}</span>` : '') +
        `</button>`;
      this.hideControls();
      this.els.result.innerHTML = this.withXsell(
        `<div class="af-confirm af-confirm--family">` +
        `<p class="af-confirm__q">✓ <strong>${escapeHtml(shown)}</strong>${year ? ` · ${year}` : ''} — which one is yours?</p>` +
        `<div class="af-opt__chips" role="group" aria-label="Which one is yours?">` +
        rows.map(chip).join('') +
        `<button type="button" class="af-chip af-chip--quiet" data-airflux-confirm-no>None of these</button>` +
        `</div></div>`);
      this.moveFocusTo(this.els.result.firstElementChild);
      this._pendingFamily = { year, car };
      track('plate_family_asked', { make: car.make, model: car.model, year, options: rows.length });
    }

    /**
     * The car IS in our catalogue — just not for what this page sells.
     *
     * Distinct from a coverage gap: we know the exact car and the exact price
     * of the thing we DO have for it, so this offers that rather than asking
     * for an email. Same shape as UX §7.1's context-aware message.
     */
    renderScopeGap(v) {
      const noun = this.scope === 'wipers' ? 'wiper blades' : 'wind deflectors';
      const otherIds = this.scope === 'wipers' ? v.d : v.w;

      // The counterpart is offered as the product card, not as a sentence: at
      // this point it is the only thing on the page the customer can act on.
      const offer = otherIds?.length ? this.crossSell(v, true) : '';

      track('coverage_gap_hit', { vehicle: v.h, scope: this.scope, via: 'stored_car' });
      // 🔴 Every gap path reports. Only two of the six did until the live audit
      // on 2026-08-19 caught it: a shopper with a stored car landing on a
      // product-line page we don't cover for them showed the gap message and
      // was counted by nothing — which is the single most common way the
      // "wanted deflectors" signal is expressed, and the reason this counter
      // exists. The success path and the plate-resolve path were covered; these
      // four were not, so the make-next list under-counted exactly the cars it
      // is meant to rank.
      this.reportCar(v);
      this.hideControls();
      this.settle();
      this.releaseReservation();
      // Painted after the innerHTML below; kept here so the mount is beside the
      // markup that creates its slot.
      this.els.result.innerHTML = this.withXsell(
        `<div class="af-gap af-gap--panel">` +
        `<p class="af-gap__msg"><strong>We don't do ${escapeHtml(noun)} for your ${escapeHtml(this.carLabel(v))} yet.</strong></p>` +
        `<p class="af-gap__sub">Tell us where to reach you and we'll let you know when we do.</p>` +
        `<div class="af-gap__capture" data-airflux-gap-slot data-car="${escapeAttr(this.carLabelWithYears(v))}" data-car-key="${escapeAttr(this.carKeyOf(v))}"${this.coverageAttrs(v)}` +
        ` data-want="${escapeAttr(this.wantScope())}"></div>` +
        (this.els.change
          ? ''
          : `<p class="af-notmine"><button type="button" class="af-notmine__btn" data-airflux-change>Not your car?</button></p>`) +
        `</div>` +
        offer);
      const slot = this.els.result.querySelector('[data-airflux-gap-slot]');
      if (slot) mountCaptureForm(slot, '', slot.dataset.car, 'coverage_gap');
    }

    /**
     * "Mine isn't listed here" — the customer's car is a version we can't
     * describe. Never guesses at a substitute; asks, and files the request.
     */
    renderNotMine() {
      const v = this._vehicle;
      const label = v ? this.carLabel(v) : 'my car';
      // 🔴 The heading keeps the SHORT name — "Tell us which Lexus UX you have"
      // — and the record keeps the dated one. A model name alone does not say
      // which Lexus UX the client is being asked about, and they have three
      // generations of some cars; the reader of the note has no page around it
      // to infer from, where the customer looking at the heading has the whole
      // buy column naming their car above it.
      const dated = v ? this.carLabelWithYears(v) : 'my car';
      track('coverage_gap_hit', { vehicle: v?.h, scope: this.scope, via: 'not_listed' });
      this.reportCar(v);
      this.els.result.innerHTML = this.withXsell(
        `<div class="af-gap af-gap--panel">` +
        `<p class="af-gap__msg"><strong>Tell us which ${escapeHtml(label)} you have.</strong></p>` +
        `<p class="af-gap__sub">We'd rather check than sell you the wrong part. Leave your email and the version you drive — we'll confirm what fits, or let you know when we stock it.</p>` +
        // 🔴 The field the sentence above has always promised. Without it this
        // state wrote "Lexus UX (version not listed)" to Shopify and stopped —
        // a record of the question with no room for the answer, on the one
        // screen where the only person who knows it was still standing.
        //
        // Not `required`: "No, or I'm not sure" in `versionAsk` lands here too,
        // and blocking that customer on a field they have just said they cannot
        // fill would cost us the address as well as the answer.
        `<div class="af-gap__capture" data-airflux-gap-slot data-car="${escapeAttr(dated)} (version not listed)" data-car-key="${escapeAttr(this.carKeyOf(v))}"${this.coverageAttrs(v)}` +
        ` data-ask-label="Which version do you have?" data-ask-placeholder="Trim, engine or your reg"` +
        ` data-want="${escapeAttr(this.wantScope())}"></div>` +
        // The way back is the LAST thing in the panel, in the same place the
        // "Mine isn't listed here" it replaced sat — one panel, one question,
        // and the exit from it always in the same corner.
        `<p class="af-notmine"><button type="button" class="af-notmine__btn" data-airflux-back>Back to what fits</button></p>` +
        `</div>` +
        // Something they can act on today, rather than only a form. Below the
        // panel rather than inside it: a white product tile inside the box that
        // asks the question reads as an answer to it, and it is not one. Still
        // inline (not below the folds) because in a gap state it is the only
        // thing on the page anyone can buy — see `crossSell`.
        //
        // "Not your car?" is NOT painted here. The theme renders it after the
        // result, which puts it under this card — deliberately, so the offer is
        // read before the way out of the page.
        this.crossSell(v, true));
      const slot = this.els.result.querySelector('[data-airflux-gap-slot]');
      if (slot) mountCaptureForm(slot, '', slot.dataset.car, 'coverage_gap');
    }

    /**
     * Which product line this request is about, where the customer said so.
     *
     * 🔴 Only ever the two real values or nothing. It comes from the page they
     * were on or the button they pressed — never from anything typed — which is
     * what makes it safe to become a Shopify tag where the free text is not: a
     * closed vocabulary of two cannot sprawl the way a slugged sentence does.
     *
     * Blank on the unscoped finder. A shopper on the home page has expressed no
     * preference between deflectors and wipers, and recording one would invent
     * demand nobody stated — the same rule `reportCar` follows for `wanted`.
     */
    wantScope() {
      return this.scope === 'deflectors' || this.scope === 'wipers' ? this.scope : '';
    }

    /**
     * "It isn't in the list at all" — the exit the cascade never had.
     *
     * The make → model → year selects are built from the fitment index, so they
     * can only ever offer cars we already make something for. A driver whose car
     * is not in there scrolls a list, fails to find themselves, and leaves —
     * and they leave without being counted, because nothing on the page was
     * identified: `/resolve` counts cars the widget recognised, and this is by
     * definition every car it did not.
     *
     * So the one thing worth asking for is the car itself, and here it is
     * `required`: an address with no car attached to it records that somebody
     * wanted something and gives the client no way to know what, which is the
     * same empty signal this whole change exists to remove.
     */
    renderNoCar() {
      track('coverage_gap_hit', { scope: this.scope, via: 'not_in_list' });
      // No `reportCar` — there is no car to report yet. The typed answer becomes
      // its own request record when the form is submitted; see `noteTypedCar`.
      this.hideControls();
      this.settle();
      this.releaseReservation();
      const noun = this.scope === 'wipers' ? 'wiper blades' : this.scope === 'deflectors' ? 'wind deflectors' : 'parts';

      // 🔴 Whatever they got AS FAR AS choosing is context we already have, and
      // it is the difference between a request that can be acted on and one
      // that cannot. Somebody who picked "Audi" and then failed to find their
      // model will write "A3 Sportback 2015" — the make is the part they think
      // they have already told us, because they have. Reading it off the select
      // means a typed answer no longer has to be self-contained to be useful,
      // and it names the make in the request even when they type two words.
      const makes = this.index?.makes;
      const make = makes && this.els.make?.value !== '' ? makes[this.els.make?.value] || '' : '';
      // `(model not listed)` rather than the bare make: this is a request for a
      // car we don't list, not a request for Audi. It is also machine-made from
      // a list of 41 makes, so unlike the typed line it is safe to slug into a
      // Shopify tag — see signupTags for why that distinction is a hard rule.
      const context = make ? `${make} (model not listed)` : '';

      this.els.result.innerHTML = this.withXsell(
        `<div class="af-gap af-gap--panel af-gap--nocar">` +
        `<p class="af-gap__msg"><strong>` +
        (make ? `Can't find your ${escapeHtml(make)}?` : `Can't find your car?`) +
        ` Tell us what you drive.</strong></p>` +
        `<p class="af-gap__sub">The list only covers cars we already make ${escapeHtml(noun)} for. ` +
        `Tell us yours and we'll let you know as soon as it's covered.</p>` +
        `<div class="af-gap__capture" data-airflux-gap-slot data-car="${escapeAttr(context)}" data-car-key=""` +
        ` data-ask-label="${make ? `Which ${escapeAttr(make)} do you have?` : 'What car do you have?'}"` +
        ` data-ask-placeholder="${make ? 'Model and year' : 'Make, model and year'}"` +
        ` data-ask-required="true"` +
        ` data-want="${escapeAttr(this.wantScope())}"></div>` +
        // 🔴 ALWAYS painted here, unlike every other gap state, and the theme's
        // own control stands down for this one — see `.af-gap--nocar` in
        // airflux-theme.css.
        //
        // "Not your car? Change it" is the wrong offer on this screen: nobody
        // has chosen a car yet, so there is nothing to change. What they need
        // is the way back to the picker they just failed to find themselves
        // in, and it belongs inside the panel with the question, the way "Back
        // to what fits" does on the state one level down.
        `<p class="af-notmine"><button type="button" class="af-notmine__btn" data-airflux-change>Back to the car picker</button></p>` +
        `</div>`);
      const slot = this.els.result.querySelector('[data-airflux-gap-slot]');
      if (slot) mountCaptureForm(slot, '', slot.dataset.car, 'coverage_gap');
      this.moveFocusTo(this.els.result.firstElementChild);
    }

    renderCoverageGap(car, anyMatch) {
      track('coverage_gap_hit', { make: car.make, model: car.model, year: car.year, scope: this.scope });
      // The demand signal that matters most: they wanted something, we didn't
      // have it. `anyMatch` is the catalogue vehicle when the car IS listed but
      // not for this product line — its absence means we don't know the car at
      // all, which is `none` by definition.
      this.reportCar(anyMatch, car);
      // The RAW register wording, deliberately: this becomes a demand signal in
      // the control panel, and "C4 PICASSO" tells the client which car they are
      // turning away where the normalised "C4" would not.
      const carLabel = `${plateCarLabel(car)}${car.year ? ` ${car.year}` : ''}`;

      // If the car is stocked for the OTHER category, offer that (§7.1).
      let otherOffer = '';
      if (anyMatch) {
        const otherIds = this.scope === 'wipers' ? anyMatch.d : anyMatch.w;
        if (otherIds?.length) {
          // Cheapest, not first — the index order is arbitrary and quoting a
          // price higher than one we sell reads as a worse offer than it is.
          // Off the collapsed list, so the link cannot land on a duplicate the
          // rest of the store hides (`quotable`).
          const p = this.quotable(otherIds, this.scope === 'wipers' ? 'd' : 'w')[0];
          const cat = this.scope === 'wipers' ? 'wind deflectors' : 'wiper blades';
          if (p)
            otherOffer = `<p class="af-gap__other">But we do have ${cat} for it — <a href="/products/${escapeAttr(p.h)}">from ${money.format(p.p / 100)}</a></p>`;
        }
      }

      const noun = this.scope === 'wipers' ? 'wiper blades' : this.scope === 'deflectors' ? 'wind deflectors' : 'parts';
      this.hideControls();
      this.settle();
      this.releaseReservation();
      this.els.result.innerHTML = this.withXsell(
        `<div class="af-gap af-gap--panel">` +
        `<p class="af-gap__msg"><strong>We don't do ${escapeHtml(noun)} for the ${escapeHtml(carLabel)} yet.</strong></p>` +
        `<p class="af-gap__sub">Tell us where to reach you and we'll let you know when we do.</p>` +
        `<div class="af-gap__capture" data-airflux-gap-slot data-car="${escapeAttr(carLabel)}" data-car-key="${escapeAttr(this.carKeyOf(anyMatch, car))}"${this.coverageAttrs(anyMatch)}` +
        ` data-want="${escapeAttr(this.wantScope())}"></div>` +
        otherOffer +
        (this.els.change
          ? ''
          : `<p class="af-notmine"><button type="button" class="af-notmine__btn" data-airflux-change>Try another car</button></p>`) +
        `</div>`);
      const slot = this.els.result.querySelector('[data-airflux-gap-slot]');
      mountCaptureForm(slot, '', slot.dataset.car, 'coverage_gap');
    }

    /**
     * Report the identified car to the demand counter.
     *
     * `v` is the catalogue vehicle when we know the car; `raw` is what the plate
     * provider said, used only when we don't. One of the two must be present.
     *
     * ⚠️ `wanted` is set ONLY where the widget was already scoped to deflectors
     * or wipers AND that line is missing for this car. An unscoped lookup — the
     * home page finder — expresses no preference between the two, and recording
     * it as wanting both would invent demand nobody stated.
     */
    /**
     * The ONE definition of a car's key.
     *
     * 🔴 Shared with the capture forms on purpose. The panel counts cars under
     * this key, and Shopify tags the customer `car:<key>` — so the panel's
     * "who asked for this car" link only works if the two agree. They did not:
     * the counter used the catalogue's vehicle handle (`ford-kuga-2020`) while
     * the tag was slugged from the display label (`ford-kuga`), and a link built
     * from one would match nothing tagged with the other. Same class of drift as
     * the five-rows-per-car bug — two systems naming the same thing differently.
     */
    carKeyOf(v, raw) {
      return v ? v.h : slugKey(`${raw ? plateCarLabel(raw) : ''} ${raw && raw.year ? raw.year : ''}`);
    }
    /**
     * What a capture slot says about the car, beside its key.
     *
     * Rides on the slot for the same reason the key does: `mountCaptureForm`
     * reads it off there, so a repainted result carries it without any call
     * site growing two more positional arguments. Blank `data-stocks` is the
     * honest answer for anything but a half-covered car — see `coverageState`.
     */
    coverageAttrs(v) {
      const { coverage, stocks } = coverageState(v);
      return ` data-coverage="${coverage}" data-stocks="${stocks}"`;
    }
    /**
     * @param {object} v - the catalogue vehicle, when we know the car
     * @param {object} [raw] - what the plate provider said, when we don't
     * @param {string} [asked] - the product line the customer asked for OUTRIGHT,
     *   for the one surface that can know it without being scoped to it. See
     *   the note on `scope` below.
     */
    reportCar(v, raw, asked) {
      const key = this.carKeyOf(v, raw);
      if (!key) return;
      // 🔴 carLabelWithYears, not carLabel. `carLabel` only appends a year when
      // the STORED car happens to carry one, so the panel was listing bare
      // "Vauxhall Astra" and "Mitsubishi Pajero" — and a model with three
      // generations in the catalogue is three different cars taking three
      // different parts. Without the years the row cannot be acted on.
      const car = v ? this.carLabelWithYears(v) : `${plateCarLabel(raw)}${raw && raw.year ? ` ${raw.year}` : ''}`;
      const { coverage, stocks } = coverageState(v);
      // Normally the PAGE says which line is in question — a deflector landing
      // page can only be asking about deflectors.
      //
      // 🔴 `asked` is the one exception, and it exists because the unscoped
      // panel had no way to express the clearest statement of intent in the
      // whole widget. On the home page the router shows "No wiper blades for it
      // yet — tell us you want them", and pressing that names a line outright.
      // The panel's own scope is 'all', so this resolved to null and the demand
      // was reported as no preference at all: the counter the client reads as
      // "N looking for wipers" ignored the one button on the site that says
      // exactly that.
      //
      // It rides the SAME per-car-per-line key the scoped pages use, so a
      // shopper who presses the button and then visits the wiper page is still
      // counted once for the day — see `dedupeResolve`.
      const named = asked === 'deflectors' || asked === 'wipers' ? asked : null;
      const scope = named || (this.scope === 'deflectors' || this.scope === 'wipers' ? this.scope : null);
      const has = scope === 'deflectors' ? v && v.d && v.d.length : scope === 'wipers' ? v && v.w && v.w.length : true;
      // `stocks` — which line we DO stock, when we stock exactly one — comes
      // from `coverageState` above. "Half covered" is not actionable on its
      // own: the operator needs to know which half is missing before they can
      // do anything about it.
      reportResolvedCar(this.plateEndpoint, { key, car, coverage, scope, stocks: stocks || null, wanted: !!scope && !has });
    }
    /**
     * The lookup's running state, ON the button that started it.
     *
     * It used to be a line of text below the button. That line does not exist
     * until the lookup starts, so starting one grew the panel by a line and
     * shoved everything under it down — a jump caused by the very tap the
     * customer just made, at the moment they are watching that spot.
     *
     * The button already has somewhere to say this: `.af-btn__label`, the same
     * span `addToCart` swaps to "Adding…". Nothing moves, and the control that
     * is busy is the one that says so.
     *
     * Measured 2026-08-27: on a phone the button is full width, so nothing moves
     * at all — the panel is 178px before, during and after. Side by side with
     * the plate on desktop it shifts by ONE pixel, because "Looking up…" is
     * almost exactly as wide as "See what fits". That is luck, not design: the
     * label is a theme setting, and a much shorter one would make the button
     * visibly shrink mid-lookup. Re-measure if it is ever renamed, rather than
     * pinning a width here — a pin cannot stop it GROWING, only shrinking, and
     * a hard width clips.
     *
     * `disabled` is NOT touched here — `lookupPlate` already owns it on both
     * sides of the fetch, and two owners of one flag is how a button ends up
     * permanently dead on a path that returned early.
     *
     * The hidden live region stays. A disabled button is not focusable and a
     * label swapped underneath one is not reliably announced, so the sentence
     * still needs somewhere to be read from — it just no longer needs to take
     * up space to do it.
     */
    setPlateMsg(text) {
      if (this.els.plateMsg) this.els.plateMsg.textContent = text;
      const btn = this.els.plateSubmit;
      if (!btn) return;
      const label = btn.querySelector('.af-btn__label') ?? btn;
      if (text) {
        // Captured from the DOM rather than hardcoded: the label is a theme
        // setting, and "See what fits" is only its default.
        btn.dataset.afRestore ??= label.textContent;
        label.textContent = text;
        btn.setAttribute('aria-busy', 'true');
        return;
      }
      if (btn.dataset.afRestore != null) label.textContent = btn.dataset.afRestore;
      delete btn.dataset.afRestore;
      btn.removeAttribute('aria-busy');
    }

    resolve() {
      const v = this.index.vehicles.find((x) => x.h === this.els.years.value);
      if (!v) return;
      track('manual_path_used', { vehicle: v.h });
      // No year on the manual path — the customer picked a generation, not a
      // year, so claiming one would be inventing it. The plate lookup DOES
      // return a specific year, and that is where §5.4's "Fits your Ford Kuga
      // 2022" wording comes from.
      this.setCar({
        handle: v.h,
        make: this.index.makes[v.m],
        model: v.n,
        body: v.b || null,
        // No `year` — the customer picked a generation. The range they picked
        // IS a fact, though, so it travels as `years` and reaches the order.
        years: `${v.f}–${v.t}`,
        label: this.label(v),
      });
      track('fit_confirmed', { vehicle: v.h });
      this.render(v);
    }

    async render(v) {
      const candidates = this.productsFor(v);
      if (!candidates.length) return;
      // A new car starts with no version chosen — carrying the last one over
      // would filter this car's products against a version it may not have.
      //
      // 🔴 But a version the customer ALREADY answered is restored from the
      // stored car. `rememberVersion()` has always written it; nothing read it
      // back, so the widget asked the same question on every page while the
      // sticky bar one line above already displayed the answer.
      if (this._vehicle?.h !== v.h) {
        const car = Car.get();
        const stored = car?.handle === v.h ? car.version : null;
        // Only if that option still exists — the catalogue may have moved on.
        this._version = stored && (v.vo ?? []).some((o) => o.l === stored) ? stored : null;
        this._unsure = false;
      }

      // The question stands down the moment we have an answer coming, and a
      // skeleton of the right shape takes its place — so the panel or the buy
      // column changes content once rather than twice (§10: zero CLS).
      this.hideControls();
      this.els.result.innerHTML = this.withXsell(
        this.layout === 'hero'
          ? `<div class="af-resolved" aria-busy="true"><div class="af-resolved__skeleton"></div></div>`
          : `<div class="af-router" aria-busy="true">` +
            `<div class="af-router__rows">${candidates
              .slice(0, 2)
              .map(() => '<div class="af-router__row af-router__row--loading"></div>')
              .join('')}</div></div>`);

      // 🔴 THE BUY BOX IS FETCHED HERE, BEFORE the products it is for.
      //
      // It used to be fetched after, and that made two round trips where the
      // network only needs one. Measured on a resolve of a Fiat 500: the
      // product fetch took 209ms and the buy box did not START until 226ms,
      // because `mountExpress` cannot ask for a handle until the dedupe has
      // picked one. On a phone against the real CDN each hop is 250–400ms, so
      // that ordering was costing the better part of a second on the one
      // interaction the whole site exists for — after the customer has already
      // told us their car.
      //
      // It does not have to wait, because NOTHING the dedupe or the sort reads
      // comes from the live product. Both read price, availability, type,
      // channel, body and pack — and the index carries all six (`c.p`, `c.a`,
      // `c.t`, `c.c`, `c.b`, `c.k`). So rank on the index first, warm the buy
      // box for the winner, and let the live fetch run alongside it.
      //
      // This is not a new trick in this file: `counterparts()` already ranks on
      // `{ available: c.a, price: c.p }` for exactly the same reason.
      //
      // Correctness does not depend on the guess being right. `buyBoxHtml`
      // memoises per handle, and `mountExpress` re-checks
      // `this._current?.p.handle !== handle` before it paints — so if a stale
      // index price makes a different product win once the live data lands, the
      // warmed box is simply never used and the real one is fetched then. The
      // worst case is one wasted request; the common case is a whole round trip
      // saved.
      //
      // Hero layout only. The router (home page) never mounts an express
      // checkout, so warming one there would be a request for nothing.
      if (this.layout === 'hero') {
        const fromIndex = new Map(
          candidates.map((c) => [c.h, { handle: c.h, available: !!c.a, price: c.p }])
        );
        const likely = this.rankCandidates(candidates, fromIndex)[0];
        if (likely) this.buyBoxHtml(likely.p.handle);
      }

      const live = await fetchProducts(candidates.map((c) => c.h));
      const byHandle = new Map(live.map((p) => [p.handle, p]));
      const shown = this.rankCandidates(candidates, byHandle);

      this._shown = shown;
      this._vehicle = v;
      // A new car invalidates whichever product was current on the old one.
      this._current = null;
      this.paintResult();
      this.writeCarUrl(v);

      // The out-of-stock capture forms are mounted by paintResult(), which runs
      // again on every chip click — mounting here too would double them up.

      // A catalogue car, resolved and showing products. `wanted` is false by
      // definition here — whatever they came for, we had it.
      this.reportCar(v);

      track('result_card_shown', { vehicle: v.h, options: shown.length });
      track('price_revealed', { vehicle: v.h, scope: this.scope });
      if (shown.length > 1) track('channel_choice_shown', { vehicle: v.h });
      if (v.vo?.length) track('version_choice_shown', { vehicle: v.h, options: v.vo.length });
    }

    /**
     * Renders the resolved car, honouring the body choice when one is offered.
     *
     * Split out from render() so picking a body re-paints without re-fetching
     * the live product data — the choice is a filter over what we already have.
     */
    /**
     * The version options as they apply to THIS placement.
     *
     * 🔴 The index precomputes `vo` from the union of deflectors AND wipers,
     * and guarantees every option leads somewhere. This widget then filters by
     * `scope` — so on a scoped landing page an option whose only covering
     * product is the other category resolved to NOTHING, under a heading that
     * had already said "✓ Fits your car". Measured on the live index: 12
     * options across 9 cars, including every non-E90 version of the 3 Series.
     *
     * Splitting each option into what is stocked HERE and what is stocked
     * elsewhere fixes both halves: the cards come from `handles`, and
     * `elsewhere` is what lets the gap message name a real alternative and a
     * real price instead of apologising into a void.
     */
    versionOptions(v) {
      if (!v?.vo?.length || !this.index) return [];
      const inScope = new Set(this.productsFor(v).map((p) => p.h));
      return v.vo.map((o) => {
        const all = (o.p ?? []).map((i) => this.index.products[i]).filter(Boolean);
        return {
          ...o,
          handles: new Set(all.filter((p) => inScope.has(p.h)).map((p) => p.h)),
          elsewhere: all.filter((p) => !inScope.has(p.h)),
        };
      });
    }

    /**
     * The options worth putting in front of somebody HERE.
     *
     * Same test the index applies at build time (`usable`), re-run against the
     * products this placement actually sells: if every answer leads to the same
     * thing the question changes nothing, so it isn't asked. 13 of the live
     * cars are in that position on one of the two landing pages.
     *
     * An option that leads nowhere here is deliberately still offered — the
     * customer has to be able to say "mine is the E91", and hearing what we DO
     * have for it is a better answer than being quietly steered to the E90.
     */
    versionQuestion(v) {
      const opts = this.versionOptions(v);
      if (opts.length < 2) return [];
      const signature = (o) => [...o.handles].sort().join(',');
      return new Set(opts.map(signature)).size > 1 ? opts : [];
    }

    /**
     * The resolved car, in whichever shape this placement is for.
     *
     * One engine, two presentations, and the split is the point of this whole
     * flow. Measured on the live index, a fully resolved car lands on exactly
     * ONE product 83% of the time on the deflectors page and 97% on the wipers
     * page — so a list of cards was a search-results UI for a catalogue that
     * almost never has a list to show, and it was being painted into two
     * containers that could not take one:
     *
     *   layout="hero"  a BUY COLUMN. The right answer is not a card in it, it
     *                  IS it — so `paintPdp` fills the column's own slots and
     *                  the page becomes that customer's product page.
     *   otherwise      a glass panel floating in a hero of fixed proportions.
     *                  Anything that grows inside stretches the composition, so
     *                  `paintRouter` answers at the height of the question it
     *                  replaces and hands off to the page built to sell.
     */
    paintResult() {
      const v = this._vehicle;
      if (!v) return;
      if (this.layout === 'hero') this.paintPdp(v);
      else this.paintRouter(v);
      this.mountCaptures();
      this.focusAnswer();
      this.settle();
    }

    /**
     * Puts the caret on the answer, once, after the question is taken away.
     *
     * Submitting hides the button the customer was standing on, and focus then
     * falls to `<body>`: a keyboard user tabs from the top of the document
     * again, and a screen-reader user is left where nothing happened. Only on
     * the FIRST paint for a car — a chip click must not steal focus back from
     * the chip that was just pressed.
     */
    focusAnswer() {
      // Not on load: a page that grabs focus before the reader has touched it
      // scrolls them somewhere they never asked to be. The order matters — the
      // once-per-car latch is only armed once they HAVE acted, or a car
      // resolved from storage would consume it and the real submit that follows
      // would move nothing.
      if (!this._userActed) return;
      if (this._focusedFor === this._vehicle?.h) return;
      this._focusedFor = this._vehicle?.h;
      this.moveFocusTo(this.els.result?.firstElementChild);
    }

    moveFocusTo(target) {
      if (!target) return;
      // Only a container needs making focusable. Stamping `tabindex="-1"` on an
      // <input> or a <button> would take it OUT of the tab order — the opposite
      // of the point.
      if (!target.matches('a[href], button, input, select, textarea, [tabindex]'))
        target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }

    /** Capture forms live inside whatever was just painted, so they remount. */
    mountCaptures() {
      for (const slot of this.els.result.querySelectorAll('[data-airflux-oos-slot]')) {
        mountCaptureForm(slot, slot.dataset.product, slot.dataset.car);
        track('out_of_stock_shown', { product: slot.dataset.product });
      }
      for (const slot of this.els.result.querySelectorAll('[data-airflux-gap-slot]'))
        mountCaptureForm(slot, slot.dataset.product || '', slot.dataset.car, 'coverage_gap');
    }

    // ── the router: what we make for this car, and where to get it ───────────

    /**
     * The standalone panel's answer — categories, never products.
     *
     * 🔴 It deliberately does NOT ask the version question, even on a car that
     * has one. Which categories we stock is *usually* a fact about the CAR, and
     * asking here would ask the same question twice — the landing page has to
     * ask it anyway before it can price anything.
     *
     * 🔴 But on 9 cars it is NOT a fact about the car, and this used to lie
     * about them. A BMW 3 Series 2005 carries a deflector for the E90 saloon
     * alone, so an E91 owner was shown "Wind deflectors £34.90", clicked it, and
     * only THEN met "we only make these for the E90". The row now carries the
     * limit in words — which costs no click and asks nothing — and where the
     * version is already known it filters the row out entirely. The nine:
     * A3 2013, 1 Series 2011, 2 Series 2014, 3 Series 2005, 3 Series 2019,
     * 5 Series 2003, 308 2022, Octavia 2020, Transporter 2004.
     *
     * 🔴 And it deliberately does NOT auto-navigate when there is only one
     * category, even though that is 83% of cars. Somebody who typed their reg
     * wanting deflectors, on one of the 80 cars we only do wipers for, would be
     * dropped on a wiper page having never been told about deflectors — and the
     * demand signal for the category we are missing, which is the most valuable
     * thing that visit could have produced, would be silently thrown away.
     */
    paintRouter(v) {
      // 🔴 FIRST product per category wins, not the last.
      //
      // This was `new Map(_shown.map((s) => [s.c.t, s]))`, and a Map built that
      // way keeps the LAST entry for a repeated key. Every car stocking two sets
      // in one category — an in-channel and a stick-on, say — therefore showed
      // the photograph of the second while the row leads to the landing page,
      // which resolves to the first. On the Citroën Dispatch the router
      // advertised the stick-on set and arrived at the in-channel one: the
      // customer taps a picture and lands on a different picture, which reads as
      // the wrong product rather than as a thumbnail bug.
      //
      // Keying by handle instead is wrong for a different reason — the row is a
      // link to a CATEGORY page for this car, and its "from" price is the
      // index's cheapest across that category, which is often not one of the
      // products fetched here. That leaves the thumb permanently empty.
      const live = new Map();
      for (const s of this._shown ?? []) if (!live.has(s.c.t)) live.set(s.c.t, s);
      const rows = [];
      const missing = [];

      // A version the customer has already told us — from a landing page, or
      // from the `?v=` on the link that brought them here. Where we have it, the
      // router narrows to it rather than qualifying: an E91 owner should not be
      // offered deflectors at all, not offered them with a footnote.
      const chosen = (v.vo ?? []).find((o) => o.l === this._version) ?? null;

      for (const t of ['d', 'w']) {
        let ids = t === 'd' ? v.d : v.w;
        if (chosen) ids = (ids ?? []).filter((i) => (chosen.p ?? []).includes(i));
        if (!ids?.length) {
          missing.push(t);
          continue;
        }
        // Quoted off what SURVIVES the duplicate collapse — see `quotable`.
        // In stock first within that: a "from" price nobody can pay is worse
        // than a slightly higher one they can.
        const priced = this.quotable(ids, t);
        const cheapest = priced[0];
        if (!cheapest) {
          missing.push(t);
          continue;
        }
        rows.push({
          t,
          noun: ROUTE[t].noun,
          href: this.carPageUrl(ROUTE[t].page, v),
          price: `${priced.length > 1 ? 'from ' : ''}${money.format(cheapest.p / 100)}`,
          // Which versions this category actually covers, where it is not all
          // of them. Only ever set when the customer has not already told us.
          limit: chosen ? null : this.versionLimit(v, t),
          // The photograph of the set this row's page will actually resolve to —
          // the first of the category, which is the one the landing page lands
          // on. Where we haven't fetched it, no thumb rather than a sibling's.
          image: live.get(t)?.p?.featured_image ?? null,
          here: window.location.pathname === ROUTE[t].page,
        });
      }

      if (!rows.length) return this.renderScopeGap(v);

      this.els.result.innerHTML = this.withXsell(
        `<div class="af-router">` +
        // The version is named where we have it, the same way the header's car
        // band names it — otherwise a customer who told us "E91" reads an answer
        // about "your BMW 3 Series" and cannot tell whether we used it.
        `<p class="af-router__lead">What we make for your <strong>${escapeHtml(
          `${this.carLabel(v)}${chosen ? ` · ${chosen.l}` : ''}`
        )}</strong></p>` +
        `<div class="af-router__rows">${rows.map((r) => this.routerRow(r)).join('')}</div>` +
        missing.map((t) => this.demandRow(t, chosen)).join('') +
        `<p class="af-router__change">` +
        `<button type="button" class="af-router__change-btn" data-airflux-change>Not your car?</button>` +
        `</p></div>`);

      // Warm the destinations. The router's whole job is a hand-off, and the one
      // cost of handing off rather than answering in place is the page load —
      // so it is paid before the customer presses anything rather than after.
      for (const r of rows) if (!r.here) prefetch(r.href);

      track('router_shown', { vehicle: v.h, categories: rows.length });
    }

    /**
     * One category, as a link to the page that sells it for this car.
     *
     * A row whose destination is the page we are already on scrolls instead —
     * the closing CTA sits at the foot of both landing pages, and the hero up
     * there has already re-resolved through `airflux:car-changed`, so there is
     * nothing to navigate to and everything to go and look at.
     */
    /**
     * "E90 · 4-door Saloon only" — where a category covers some versions, not all.
     *
     * The words are the version option's own label, so the row says exactly what
     * the landing page will say and what the dropdown already said. It never
     * guesses a body for a bare chassis code: an unexplained code prints as the
     * code, which is honest, and `scripts/lib/chassis-bodies.mjs` is the one
     * place a body may be added — from the client, never from inference.
     */
    versionLimit(v, t) {
      if (!v.vo?.length) return null;
      const covers = v.vo.filter((o) => (o.p ?? []).some((i) => this.index.products[i]?.t === t));
      // Everything, or nothing we can describe — say nothing either way. A
      // qualifier that names every option is noise, and one that names none
      // would be a claim we cannot support.
      if (!covers.length || covers.length === v.vo.length) return null;
      return `${covers.map((o) => o.l).join(', ')} only`;
    }

    routerRow(r) {
      const inner =
        `<span class="af-router__thumb">` +
        (r.image
          ? `<img src="${escapeAttr(r.image)}&width=160" alt="" width="80" height="80" loading="lazy">`
          : '') +
        `</span>` +
        `<span class="af-router__text">` +
        `<span class="af-router__name">${escapeHtml(r.noun)}</span>` +
        `<span class="af-router__price">${escapeHtml(r.price)}</span>` +
        (r.limit ? `<span class="af-router__limit">${escapeHtml(r.limit)}</span>` : '') +
        `</span>` +
        `<span class="af-router__go" aria-hidden="true">${ARROW}</span>`;

      return r.here
        ? `<button type="button" class="af-router__row" data-airflux-scroll>${inner}</button>`
        : `<a class="af-router__row" href="${escapeAttr(r.href)}">${inner}</a>`;
    }

    /**
     * The category we do NOT make for this car — as a request, not an apology.
     *
     * This is the row that stops a single-category answer reading as a shrug,
     * and it is where the demand signal comes from: 80 cars in the catalogue
     * carry wipers and no deflectors, and those are exactly the customers whose
     * intent the client most needs to hear about.
     */
    demandRow(t, chosen) {
      // Names the version where the customer has given us one — "no deflectors
      // for the E91 yet" is a fact they can act on, where "for it" would leave
      // them wondering whether we mean their car or their version.
      const what = chosen ? `the ${escapeHtml(chosen.l)}` : 'it';
      return (
        `<p class="af-router__demand">No ${escapeHtml(ROUTE[t].noun.toLowerCase())} for ${what} yet — ` +
        `<button type="button" class="af-router__demand-btn" data-airflux-demand="${t}">tell us you want them</button></p>`
      );
    }

    renderDemand(t) {
      const v = this._vehicle;
      const noun = ROUTE[t].noun.toLowerCase();
      // The version rides along on the demand signal — "3 Series · E91" is the
      // car the client is being asked to source for, and the bare model is not.
      const label = v ? `${this.carLabelWithYears(v)}${this._version ? ` · ${this._version}` : ''}` : 'my car';
      track('coverage_gap_hit', { vehicle: v?.h, scope: ROUTE[t].scope, via: 'router_demand' });
      // They pressed the button for this line by name — see `reportCar`.
      this.reportCar(v, null, ROUTE[t].scope);
      this.els.result.innerHTML = this.withXsell(
        `<div class="af-router">` +
        `<p class="af-router__lead"><strong>We'll tell you when we do ${escapeHtml(noun)} for your ${escapeHtml(label)}.</strong></p>` +
        // 🔴 The noun is NOT glued onto the car any more — `data-want` carries it,
        // and the Worker composes the sentence. This panel is the one place a
        // shopper names a product line outright (they pressed the button for
        // it), and the record used to say so only inside a label nothing could
        // filter on. Leaving it in both would read "cover wind deflectors for
        // BMW 3 Series — wind deflectors".
        //
        // `ROUTE[t].scope`, not `this.scope`: this panel is unscoped, and the
        // preference is the button they pressed rather than the page they are
        // on — the one case where `wantScope()` would answer blank and be wrong.
        `<div class="af-router__capture" data-airflux-gap-slot data-car="${escapeAttr(label)}" data-car-key="${escapeAttr(this.carKeyOf(v))}"${this.coverageAttrs(v)}` +
        ` data-want="${escapeAttr(ROUTE[t].scope || '')}"></div>` +
        `<p class="af-router__change">` +
        `<button type="button" class="af-router__change-btn" data-airflux-back>Back</button></p>` +
        `</div>`);
      this.mountCaptures();
    }

    /** A landing page addressed to this car, version included where known. */
    carPageUrl(page, v) {
      const url = new URL(page, window.location.origin);
      url.searchParams.set('car', v.h);
      if (this._version) url.searchParams.set('v', this._version);
      return url.pathname + url.search;
    }

    /**
     * "We don't do this for your exact version — here's what we do have."
     *
     * Same shape as `renderScopeGap`, one level finer: that one answers "we
     * don't stock this category for your car", this one answers it for the
     * version they just told us they drive. Because the index knows which
     * products cover the option, the alternative is a real product at a real
     * price rather than a consolation link (UX §7.1).
     */
    versionGap(v, option) {
      const noun = this.scope === 'wipers' ? 'wiper blades' : 'wind deflectors';
      const label = `${this.carLabelWithYears(v)} · ${option.l}`;

      track('coverage_gap_hit', { vehicle: v.h, scope: this.scope, version: option.l, via: 'version' });
      this.reportCar(v);
      // 🔴 The CONTENTS of the panel, not the panel — and no counterpart card.
      // Both are the caller's, because this state has two shapes: alone it is
      // its own panel, and under a chip row it is the tail of the panel the
      // chips already opened. The card is never in either: it is a white
      // product tile and a thing to buy, and a thing to buy inside the box that
      // asks the question reads as the answer to it.
      return (
        `<p class="af-gap__msg"><strong>We don't do ${escapeHtml(noun)} for the ${escapeHtml(option.l)} yet.</strong></p>` +
        `<p class="af-gap__sub">Leave your email and we'll tell you when we do.</p>` +
        `<div class="af-gap__capture" data-airflux-gap-slot data-car="${escapeAttr(label)}" data-car-key="${escapeAttr(this.carKeyOf(v))}"${this.coverageAttrs(v)}` +
        ` data-want="${escapeAttr(this.wantScope())}"></div>`
      );
    }

    /**
     * The landing-page hero, repainted for whatever is actually visible.
     *
     * Called from paintResult rather than render, so a version choice that
     * narrows the cards narrows the price with them. It used to be set once,
     * which left "£34.90" on screen beside an E91 that has no deflector at all.
     *
     * The gallery gets the resolved product's WHOLE media list, not just its
     * featured image. That is the difference between a landing page that looks
     * like a product page and one that is a product page: a shopper deciding on
     * a set of deflectors for their own car wants the fitted shot and the
     * profile detail, and those are photographs two to seven.
     */
    paintHero(current) {
      if (this.layout !== 'hero') return;

      if (this.els.gallery) {
        // Left on the element rather than called as a method: this file is
        // loaded from the document head and `airflux-gallery.js` from the
        // section, so on a returning visit the engine can resolve a car before
        // the element has upgraded. Its `connectedCallback` picks this up.
        const media = current ? galleryMedia(current.p) : null;
        this.els.gallery._media = media;
        this.els.gallery.setMedia?.(media);
      } else if (this.els.hero && !current && this._heroDefault !== null) {
        this.els.hero.innerHTML = this._heroDefault;
        this.els.hero.classList.toggle('af-gallery__figure--bleed', this._heroDefaultBleed);
      } else if (this.els.hero && current?.p.featured_image) {
        this.els.hero.innerHTML =
          `<img class="af-gallery__img" src="${escapeAttr(current.p.featured_image)}&width=800" alt="${escapeAttr(
            current.p.title || ''
          )}" width="800" height="800">`;
      }
      if (!this.els.price) return;
      if (!current) {
        // Nothing to price. Fall back to the range for this page rather than
        // leaving the previous selection's figure standing.
        this.showFromPrice();
        return;
      }
      // ONE number. This used to print every candidate's price side by side —
      // "£34.90 / £44.90" — which is what a page with no option chosen has to
      // do. The chips below choose one, so the price above them is that one's,
      // and it changes with them.
      this.els.price.innerHTML = this.priceMarkup(
        current.p.variants?.[0]?.price ?? current.p.price,
        current.p.variants?.[0]?.compare_at_price ?? current.p.compare_at_price
      );
    }

    // ── the landing page, become this customer's product page ────────────────

    /**
     * Fills the buy column's own slots rather than appending a card to it.
     *
     * The column already has every slot a product page needs — a title, a fit
     * line, a price, an action, promises — and until a car is known they are
     * simply generic. Resolving fills them in. That is why nothing here draws a
     * bordered card: a card inside a buy column is a product page inside a
     * product page, which is what put two images, two prices and a "view full
     * details" link pointing at the page the customer was already on.
     *
     * The order below is the real product page's order, deliberately:
     *
     *     title(+car) → fit → price → options → stock → buy → cross-sell
     */
    paintPdp(v) {
      const shown = this._shown ?? [];
      const chosen = this._version ?? null;
      const unsure = this._unsure === true;
      const options = this.versionQuestion(v);
      this._options = options;
      const chosenOpt = options.find((o) => o.l === chosen) ?? null;

      // A car with a version question is NOT resolved until it is answered.
      const pending = options.length > 0 && !chosen && !unsure;

      // ── pending: one question, nothing else ──────────────────────────────
      if (pending) {
        this.paintTitleCar(null);
        this.paintFitLine(null);
        this.paintHero(null);
          this.paintDetails(null);
        // The confirmation shape already carries the escape as its second
        // button, so "Mine isn't listed here" underneath would be the same exit
        // written twice. The chip-row shape still needs it.
        const asksOne = options.filter((o) => o.handles.size).length === 1;
        this.els.result.innerHTML = this.withXsell(
          `<div class="af-resolved">${this.versionAsk(v, options, asksOne ? '' : this.notMyCar(v))}</div>`);
        this._current = null;
        this._optionCount = 0;
        this.announce(false);
        this.releaseReservation();
        return;
      }

      // ── the products this answer leaves on the table ─────────────────────
      let visible = chosenOpt ? shown.filter((s) => chosenOpt.handles.has(s.c.h)) : shown;
      let depends = [];
      if (unsure) {
        // "Not sure" is an answer in its own right, not a shrug — but it may
        // only ever offer a product that covers EVERY version of this car.
        //
        // 🔴 This used to intersect over `options.filter((o) => o.handles.size)`
        // — the versions we stock something for on THIS page — which is a
        // different question and a dangerous one. A BMW 3 Series 2005 has four
        // versions and one deflector, for the E90 saloon; filtering left E90 as
        // the only option, so its deflector trivially "covered every version"
        // it was asked about. The page then read "✓ Fits your BMW 3 Series ·
        // fits every version" directly above "Fits: E90 only · 4-door Saloon",
        // and sold a saloon kit to an E92 coupé. Measured on the live index: 5
        // of the 32 version cars on the deflectors page and 3 of the 5 on wipers
        // were in that state.
        //
        // Intersecting over ALL the options is the honest test. Where an option
        // has nothing stocked here the intersection is empty and there is
        // nothing safe to sell — which is the right answer, not a bug.
        const universal = new Set(
          options.length ? [...options[0].handles].filter((h) => options.every((o) => o.handles.has(h))) : []
        );
        visible = shown.filter((s) => universal.has(s.c.h));
        depends = shown.filter((s) => !universal.has(s.c.h));
      }

      // ── the version they picked, which we make nothing for ───────────────
      if (!visible.length) {
        this.paintTitleCar(null);
        this.paintFitLine(null);
        this.paintHero(null);
          this.paintDetails(null);
        const oneOnly = options.filter((o) => o.handles.size).length === 1;
        const escape = this.notMyCar(v);
        const gapInner = chosenOpt ? this.versionGap(v, chosenOpt) : this.unsureGap(v);
        // ONE panel, never two stacked. The chips and the answer they produced
        // are the same question — "which version?" and "that one we don't make"
        // — so the consequence is the tail of the box the chips opened rather
        // than a second box under it. Where the chips are suppressed there is
        // no box yet, so the gap opens its own.
        //
        // The counterpart card is outside either way: it is the only thing on
        // this page anyone can buy, and it is not part of the question.
        const offer = chosenOpt ? (chosenOpt.elsewhere.length ? this.crossSell(v, true) : '') : this.crossSell(v, true);
        this.els.result.innerHTML = this.withXsell(
          `<div class="af-resolved">` +
          (oneOnly
            ? `<div class="af-gap af-gap--panel">${gapInner}${escape}</div>`
            : this.versionChooser(v, options, `<div class="af-gap">${gapInner}</div>${escape}`)) +
          offer +
          `</div>`);
        this._current = null;
        this.announce(false);
        this.releaseReservation();
        return;
      }

      // ── resolved ─────────────────────────────────────────────────────────
      // The current product survives a repaint where it can, so clicking a
      // "Type" chip does not silently reset the "Set" chip beside it.
      if (!this._current || !visible.includes(this._current)) this._current = visible[0];
      const cur = this._current;
      const axes = this.axesFor(visible);
      // What a chip click may choose between, and what to warm the express
      // buy box for. Held on the instance because `chooseAxis` runs from a
      // click, long after this paint has returned.
      this._visibleForAxes = visible;
      this._optionCount = visible.length;

      this.paintTitleCar(v);
      this.paintFitLine(v, chosen, unsure);
      this.paintHero(cur);

      const available = !!cur.p.variants?.[0]?.available;

      // A confirmed single-version car does not get the question back — the fit
      // badge above already names it, and "Mine isn't listed here" below is the
      // way out. Repeating "is that your car? [Yes ✓]" over an answered
      // question is the page asking twice.
      const stocked = options.filter((o) => o.handles.size);
      // 🔴 The escape has ONE home per car, and which one depends on whether
      // there is a question to escape from.
      //
      // Where the chips are asked it belongs INSIDE their box, as the last
      // answer to the question they ask — which is where the other two states
      // that draw them have always put it (`versionAsk` and the version gap).
      // The resolved state was the odd one out, sending it to the `Fits:` row
      // instead, so answering the question moved the link out of the panel it
      // had just been part of.
      //
      // Where the chips are NOT asked — a single stocked version, or a row
      // whose chassis codes span generations the catalogue never names as
      // options — there is no box, and the `Fits:` row stays its home. Those
      // are the cars `notMyCar` exists for most: nothing on screen offers a
      // version to pick, and the owner of an unlisted one still needs a way to
      // say so.
      const escape = this.notMyCar(v);
      const chooser = stocked.length === 1 ? '' : this.versionChooser(v, options, escape);
      const axisHtml = axes.map((a) => this.axisChips(a, cur, axes)).join('');

      this.els.result.innerHTML = this.withXsell(
        `<div class="af-resolved">` +
        chooser +
        // "Which one is yours?" first, then "Set" — the car before the taste.
        //
        // Normally the axis chips go to the theme's slot under the price and
        // the version box stays here, which puts them in that order on screen
        // reversed: preference above fitment. Where BOTH render, the axes come
        // inline instead, directly under the box, so the page asks what car you
        // have before it asks which set you want. Nothing moves on a car with
        // no version question — `optionChips` still routes to the slot there,
        // and `withXsell` clears the slot in lockstep either way, so the chips
        // can never be drawn in both places at once.
        (chooser ? axisHtml : this.optionChips(axisHtml)) +
        this.coverageNote(cur, depends.length, v, chooser ? '' : escape) +
        (available
          ? // The theme's own stock row and primary pill, not a shape of this
            // flow's invention — the resolved landing page has to be the same
            // product page the customer would have reached by any other route.
            // `.af-pdp__meta` is the product page's row, with the same two
            // things on it in the same order.
            `<div class="af-pdp__meta"><p class="af-pdp__stock">In stock</p>` +
            this.dispatchRow() +
            `</div>` +
            `<div class="af-resolved__buy">` +
            `<button type="button" class="af-btn af-btn--dark af-resolved__add" ${this.addAttrs(cur, v)}>` +
            `${CART}<span class="af-btn__label">Add to basket</span></button>` +
            `<div class="af-resolved__express" data-airflux-express></div>` +
            `</div>` +
            // The guarantee line, under the button and inside the AVAILABLE
            // branch — the same place and the same claim the product page puts
            // there. Under a sold-out result it would promise a fit for
            // something nobody can buy.
            this.assuranceRow()
          : /* 🔴 The SAME markup `snippets/airflux-pdp-oos.liquid` renders, and
               that is the whole point of it looking like this rather than like
               a string somebody wrote here.

               Out-of-stock had two renderings of one state: the product page's
               Liquid snippet drew a muted card with the heading and the form
               inside it, and this line drew a bare red sentence followed by an
               unstyled `<div>`. Same shopper, same sold-out product, two
               different screens depending only on whether they arrived via
               /products or via a landing page that resolved their car. On the
               landing page the sign-up form had nothing grouping it to the
               sentence above it, so it floated on the page ground with the exit
               link reading as a third loose thing.

               The card's CSS was scoped to `.af-pdp--product` as well, which
               locked it in — emitting the right class here would still have
               rendered unstyled until that scope was widened to
               `.af-pdp__result` too. Both halves changed together. */
            `<div class="airflux-pdp__oos">` +
            `<p class="airflux-pdp__oos-heading">Out of stock for your ${escapeHtml(this.carLabelDated(v))}</p>` +
            `<div data-airflux-oos-slot data-product="${escapeAttr(cur.p.handle)}" data-car="${escapeAttr(
              this.carLabelDated(v)
            )}"></div>` +
            `</div>`) +
        // Two things leave this block on a landing page.
        //
        // The cross-sell goes to the slot below the folds, because it is a
        // second thing to buy and belongs after the reference material rather
        // than between the button and it. `crossSell()` decides that, not this
        // call site — see `withXsell`.
        //
        // "Full details for this set" is dropped entirely: the folds now carry
        // the whole catalogue title and the spec table, so the link promised a
        // page the reader is already reading. It stays on the closing CTA's
        // panel, which has no folds of its own and where the product page is
        // the only route to any of it.
        this.crossSell(v) +
        (this.layout === 'hero' ? '' : this.moreLink(cur)) +
        `</div>`);

      this.paintDetails(cur, v, chosen);
      this.rememberSeen(v, chosen, cur);

      // The sticky bar turns into a real buy bar off the back of this, and
      // anything else on the page can follow the same event rather than
      // reaching into markup the engine rewrites on every chip click.
      this.announce(available, `${this.carLabel(v)}${chosen ? ` · ${chosen}` : ''}`, cur.c.c ?? null, cur.p?.handle ?? null);

      // Shopify's own express checkout, server-rendered for the current product
      // and dropped in beside our Add to cart. It can only be fetched, never
      // faked: `{{ form | payment_button }}` is the only thing that can mint a
      // one-tap checkout, and it needs a real product context to do it.
      if (available) this.mountExpress(cur, v);
    }

    /**
     * The one-line snapshot the pre-paint script reads on the next visit.
     *
     * Only the two things that visibly rebuild themselves — the photograph and
     * the price — and only for THIS scope, keyed by the car and version it was
     * true for, so a customer who changes car never sees the last one's
     * picture. It is a hint, not a source of truth: the engine paints over it
     * with live data a moment later, and a stale price that corrects itself in
     * 300ms is invisible where a jump is not.
     */
    rememberSeen(v, version, entry) {
      if (this.layout !== 'hero') return;
      this._seen = {
        car: v.h,
        version: version || '',
        img: entry?.p?.featured_image ? `${entry.p.featured_image}&width=800` : null,
        // The now-price alone, not the row's text. Since the row grew a
        // strikethrough and a savings pill, its `textContent` is the three of
        // them run together — "£34.90£43.90Save 21%" — which is what would have
        // been stored and, on the next visit, restored.
        price: this.els.price?.querySelector('.af-price__now')?.textContent ?? '',
      };
      this.writeSeen();
      // Heights are taken later — the buy box and the counterpart card are each
      // a fetch away, and they are the two biggest movers.
      this.recordHeights();
    }

    /**
     * How tall the answer turned out, so the next visit can reserve it.
     *
     * 🔴 Holding back the price and the field was not enough: the column still
     * moved four times on every load. Measured on the live page, CLS 0.14 —
     * the field collapsing took 194px out from under everything below it, the
     * result filling put 60px back, the express button another 68, and the
     * counterpart card, which lands last after its own request, a further 179.
     *
     * Measured AFTER those two fetches, and debounced, so the figure stored is
     * the settled height rather than an intermediate one.
     *
     * 🔴 Stored with the viewport it was measured at, and only reused within
     * 80px of it. The buy column is half the page above 990px and all of it
     * below, so a height learned on a desktop would reserve roughly twice what
     * a phone needs — a reserved gap that never fills is worse than the shift
     * it was meant to prevent.
     */
    recordHeights() {
      if (this.layout !== 'hero' || !this._seen) return;
      clearTimeout(this._measureTimer);
      this._measureTimer = setTimeout(() => {
        requestAnimationFrame(() => {
          if (!this._seen || !this.els.result?.isConnected) return;
          const round = (el) => Math.round(el?.getBoundingClientRect().height ?? 0);
          this._seen.fitH = round(this.els.fitline);
          this._seen.resultH = round(this.els.result);
          this._seen.w = window.innerWidth;
          this.writeSeen();
          // Measured means settled, so the reservation has done its job.
          this.releaseReservation();
        });
      }, 250);
    }

    /* Through `AirfluxStorage` like the car, and for the same reason: this
       record names the car the visitor resolved, so leaving it in localStorage
       after a "Reject all" would keep on disk exactly what the car key was
       just cleared of. Without consent it lives in the session — the pre-paint
       optimisation still works for the rest of the visit, it just doesn't
       survive the tab. That costs a returning rejecter one frame of layout
       shift and nothing else. */
    writeSeen() {
      window.AirfluxStorage?.writeJSON(`airflux:seen:${this.scope}`, this._seen);
    }

    /** Hands the page back to the engine, whatever it ended up painting. */
    /**
     * Hands the page back — in two stages, because they finish at two times.
     *
     * `af-car-resolving` holds back what the FIRST paint would get wrong: the
     * stand-in photograph, the range price, the field. All three are correct
     * the moment the engine paints, so the class comes off there.
     *
     * 🔴 `af-car-reserving` holds the HEIGHT, and must outlive that paint. The
     * buy box and the counterpart card each arrive on their own request, so at
     * first paint the column is ~250px shorter than it is about to be —
     * releasing the reservation there just moved the shift later and turned one
     * jump into a shrink followed by two grows. It is released by
     * `recordHeights`, once the column has actually stopped changing.
     */
    settle() {
      const root = document.documentElement;
      root.classList.remove('af-car-resolving');
      root.style.removeProperty('--af-car-img');
    }

    releaseReservation() {
      const root = document.documentElement;
      root.classList.remove('af-car-reserving');
      root.style.removeProperty('--af-car-fit-h');
      root.style.removeProperty('--af-car-result-h');
    }

    /**
     * Tells the page whether there is now something on it to buy.
     *
     * `channel` rides along because the fitting section below is written per
     * deflector type, and this is already the moment the page learns which one
     * — it fires again on every chip click, so a car stocking both kinds keeps
     * the steps in step with the "Type" chip. Null off a wiper, off an
     * unresolved car, and off "Not your car?", all of which mean the same thing
     * downstream: ask, do not assume.
     */
    announce(buyable, car = null, channel = null, product = null) {
      if (this.layout !== 'hero') return;
      // `product` is the handle of the set now on the page, so the reviews
      // wall further down can turn from the category's wall into this set's
      // — without it the buy box said "Ford Mondeo" while the reviews still
      // said nothing about the set (2026-09-03). Null off everything that is
      // not a buyable set, which the wall reads as "back to the category".
      const detail = { buyable: !!buyable, car, channel, product };
      // 🔴 A snapshot as well as an event. Off `?car=` this fires while the
      // page is still loading, and a listener further down — the reviews wall,
      // whose script is deferred behind this one — may not exist yet. Measured
      // on the dev theme (2026-09-03): the same URL caught the event on one
      // load and missed it on the next. Anything that connects late reads
      // this instead of waiting for a repeat that never comes.
      window.AirfluxResolved = detail;
      document.dispatchEvent(new CustomEvent('airflux:resolved', { detail, bubbles: true }));
    }

    /**
     * The version question, asked in the shape the answer actually has.
     *
     * 🔴 Four chips where only one leads anywhere is not a choice, it is a
     * trap with three wrong doors. A BMW 3 Series 2005 offers E90, E91, E92 and
     * E93 and we make a deflector for the E90 alone: three of those chips exist
     * only to tell you no, after you have committed to a click. Measured on the
     * live index, 8 cars across the two landing pages are in that position.
     *
     * So where exactly ONE version is stocked here, the honest question is not
     * "which of these four?" but "is yours the one we make?" — the constraint
     * stated up front instead of hidden behind a chip. Where two or more are
     * stocked it is a real choice and stays a chip row.
     */
    versionAsk(v, options = this.versionQuestion(v), escape = '') {
      const stocked = options.filter((o) => o.handles.size);
      if (stocked.length !== 1)
        return (
          `<p class="af-resolved__ask">Your ${escapeHtml(this.carLabelWithYears(v))} came in more than one version.</p>` +
          this.versionChooser(v, options, escape)
        );

      const only = stocked[0];
      const name = [only.c, only.b].filter(Boolean).join(' · ') || only.l;
      return (
        `<div class="af-opt af-opt--ask">` +
        `<p class="af-resolved__ask">We make these for the <strong>${escapeHtml(name)}</strong> only.<br>` +
        `Is that your ${escapeHtml(this.carShort(v))}?</p>` +
        `<div class="af-opt__chips" role="group">` +
        `<button type="button" class="af-chip af-chip--yes" data-airflux-version="${escapeAttr(only.l)}">` +
        `Yes, that's mine</button>` +
        `<button type="button" class="af-chip af-chip--quiet" data-airflux-notmine>No, or I'm not sure</button>` +
        `</div></div>`
      );
    }

    /** The `<h1>`'s quiet half becomes their car, or goes back to the copy. */
    /**
     * The `<h1>`'s quiet half — "for your Ford Kuga 2020–2026".
     *
     * Dated for the same reason the product page's heading is (see `hero_car`
     * in `airflux-product-hero.liquid`, which is this line's server-rendered
     * twin): a model name alone does not tell a shopper WHICH Kuga they are
     * being sold, on a page whose whole promise is that it knows. Here the
     * exact year wins where the plate gave us one, so a reg lookup reads "for
     * your Ford Kuga 2022" — better than any range, and the only place either
     * page can say it.
     */
    paintTitleCar(v) {
      if (!this.els.titleCar) return;
      this.els.titleCar.textContent = v ? `for your ${this.carLabelDated(v)}` : this._titleCarDefault;
    }

    /**
     * "✓ Fits your BMW 1 Series 2011–2019" — above the price, as on a real PDP.
     *
     * It is outside `[data-airflux-result]` because it answers "is this page
     * about my car", which does not change when a chip does. Repainting it with
     * the buying furniture made it flash on every option click.
     */
    paintFitLine(v, chosen, unsure) {
      if (!this.els.fitline) return;
      if (!v) {
        this.els.fitline.innerHTML = '';
        return;
      }
      const qualifier = chosen ? ` · ${escapeHtml(chosen)}` : unsure ? ' · fits every version' : '';
      this.els.fitline.innerHTML =
        `<p class="af-fit">${CHECK}<span>Fits your ${escapeHtml(this.carLabelDated(v))}${qualifier}</span></p>`;
    }

    /**
     * The axes that genuinely vary across the products still on the table.
     *
     * Two kinds, and telling them apart is the whole design:
     *
     *   PREFERENCE  channel, pack size — both answers fit, so they are ordinary
     *               variant chips and a wrong click costs nothing.
     *   FITMENT     body — a wrong answer ships the wrong part, so it is asked
     *               as a question and carries the same "how do I tell?" help the
     *               version question does.
     *
     * Measured after the version question is answered: 73 of the 75 landing-page
     * cars that still have a choice are choosing channel, which is why this is a
     * chip row and not a list of cards.
     */
    axesFor(list) {
      if (list.length < 2) return [];
      const wipers = list.every((s) => s.c.t === 'w');
      const defs = [
        {
          key: 'c',
          name: 'channel',
          label: 'Type',
          text: (val) => (val === 'out-channel' ? 'Out-Channel' : 'In-Channel'),
          help: 'In-Channel deflectors sit inside the window channel; Out-Channel ones mount to the outside of the door frame. Both fit your car — it is down to preference.',
        },
        {
          key: 'k',
          name: 'pack',
          label: 'Set',
          text: (val) => (wipers ? `${val} blades` : `${val}-piece`),
          help: wipers
            ? 'The number of blades in the box.'
            : 'A 2-piece set covers the front windows. A 4-piece set covers all four.',
        },
        {
          key: 'b',
          name: 'body',
          label: 'Which shape is yours?',
          fitment: true,
          text: (val) => val,
          help: "Count the doors, boot included — a 5-door hatchback has a tailgate, an estate has a long roof over the boot. If yours is not here, tell us and we'll check.",
        },
      ];

      return defs
        .map((d) => ({
          ...d,
          values: [...new Set(list.map((s) => s.c[d.key]).filter((x) => x !== null && x !== undefined))],
        }))
        .filter((d) => d.values.length > 1);
    }

    axisChips(axis, cur, axes = [axis]) {
      const selected = cur.c[axis.key];
      const others = axes.filter((a) => a.key !== axis.key);
      const pool = this._visibleForAxes ?? [];

      /**
       * What happens if you press this chip, given everything else you picked.
       *
       * 🔴 The combinations are not a complete grid. Four cars stock, say,
       * in-channel only as a 2-piece and out-channel in both — so pressing
       * "In-channel" while a 4-piece is selected silently moved the Set chip
       * too. The customer asked for one change and got two, with nothing on
       * screen to warn them.
       *
       * ⚠️ Marked, NOT disabled. Two of those four cars stock only the DIAGONAL
       * pair (Defender 90 is in-channel 4-piece and out-channel 2-piece, and
       * nothing else; ProAce City Verso is the mirror of it). Make an
       * unreachable chip unclickable there and every other chip is unreachable
       * too — the customer is stranded on whichever product they landed on,
       * with the other one unbuyable. So the chip still works; it just says
       * first what it is about to do.
       */
      const shift = (val) => {
        if (!others.length) return null;
        const matches = pool.filter((s) => String(s.c[axis.key]) === String(val));
        if (!matches.length) return null;
        const keeps = matches.find((s) => others.every((a) => s.c[a.key] === cur.c[a.key]));
        if (keeps) return null;
        const moved = others.filter((a) => matches[0].c[a.key] !== cur.c[a.key]);
        return moved.map((a) => a.text(matches[0].c[a.key])).join(', ') || null;
      };

      return (
        `<div class="af-opt${axis.fitment ? ' af-opt--ask' : ''}">` +
        `<p class="af-opt__label">${escapeHtml(axis.label)}` +
        (axis.help
          ? ` <button type="button" class="af-opt__help-btn" data-airflux-helper>${INFO}<span class="af-visually-hidden">What's the difference?</span></button>`
          : '') +
        `</p>` +
        `<div class="af-opt__chips" role="group" aria-label="${escapeAttr(axis.label)}">` +
        axis.values
          .map((val) => {
            const moves = val === selected ? null : shift(val);
            const label = escapeHtml(axis.text(val));
            const note = moves ? `only with ${moves}` : null;
            return (
              `<button type="button" class="af-chip${val === selected ? ' is-selected' : ''}${moves ? ' is-elsewhere' : ''}"` +
              ` data-airflux-axis="${axis.key}" data-airflux-axis-value="${escapeAttr(String(val))}"` +
              (note ? ` title="${escapeAttr(`${axis.text(val)} — ${note}`)}"` : '') +
              ` aria-pressed="${val === selected}">${label}` +
              (note ? `<span class="af-visually-hidden"> — ${escapeHtml(note)}</span>` : '') +
              `</button>`
            );
          })
          .join('') +
        `</div>` +
        (axis.help ? `<p class="af-opt__help" hidden>${escapeHtml(axis.help)}</p>` : '') +
        `</div>`
      );
    }

    /**
     * Picks the product a chip stands for.
     *
     * Selection is by PRODUCT, never by a set of axis values, because the
     * combinations are not a complete grid: a car can stock in-channel in both
     * pack sizes and out-channel in one. Choosing the product keeps every chip
     * click landing on something real, and it prefers the product that keeps
     * the other chips where the customer left them.
     */
    chooseAxis(key, value) {
      const visible = this._visibleForAxes ?? [];
      const cur = this._current;
      const pool = visible.filter((s) => String(s.c[key]) === String(value));
      if (!pool.length) return;
      const others = this.axesFor(visible).filter((a) => a.key !== key);
      this._current =
        pool.find((s) => others.every((a) => s.c[a.key] === cur?.c[a.key])) ?? pool[0];
      this.paintResult();
    }

    /**
     * "Fits: F20 only" — what THIS kit covers, where that is worth saying.
     *
     * 🔴 Suppressed where it only repeats the answer the customer just gave.
     * On an A3 that has picked "5-door Hatchback" the page was stating that
     * fact three times in five lines — the fit badge, the selected chip, and
     * this line — which reads as a page arguing with itself. It survives where
     * it ADDS something: a kit whose chassis codes cover less of the row than
     * the row spans ("F20 only"), or a body the version question never asked
     * about.
     */
    coverageNote(cur, dependsCount, v = this._vehicle, escape = this.notMyCar(v ?? this._vehicle)) {
      const chosen = this._version ?? null;
      const partial = this.partialCover(cur.c);
      const code = cur.c.g && (partial || cur.c.g !== chosen) ? `${cur.c.g}${partial ? ' only' : ''}` : null;
      // Silent where the fit line already names this body. That line is
      // `label(v)`, which appends the CAR's body when it has one — so on an
      // Audi A3 Saloon/Hatchback the page said "Fits your Audi A3
      // Saloon/Hatchback" and then, four lines down, "Fits: Saloon/Hatchback".
      const named = this.carLabel(v ?? this._vehicle);
      const body = cur.c.b && cur.c.b !== chosen && !named.includes(cur.c.b) ? cur.c.b : null;
      const parts = [code, body].filter(Boolean);
      // "Mine isn't listed here" rides on this row, right-aligned — but only on
      // the cars that never get asked the version question.
      //
      // It used to be the last thing in `.af-resolved`, under the cross-sell —
      // several screens below the version chips it is the answer to, which read
      // as a footer rather than as the escape from a question just asked. Then
      // it lived here unconditionally, which was right for a car with no chips
      // and wrong for one with them: the caller now decides, and passes '' when
      // the chip box has taken it in. See the resolved branch of `paintPdp`.
      //
      // The row renders when EITHER half has something to say, so a car with no
      // coverage note still gets the escape, and a car whose escape went into
      // the box still gets its coverage note.
      const spec = parts.length
        ? `<p class="af-resolved__spec"><strong>Fits:</strong> ${escapeHtml(parts.join(' · '))}</p>`
        : '';
      return (
        (spec || escape ? `<div class="af-resolved__fitrow">${spec}${escape}</div>` : '') +
        (dependsCount ? this.dependsNote(dependsCount) : '')
      );
    }

    /**
     * `<airflux-dispatch>`, built here so it can sit on the stock row.
     *
     * Emitted ONLY from the branch that has something to dispatch. A car we
     * stock nothing for, or a version we make nothing for, must not carry a
     * "next dispatch Monday" promise about a part that does not exist — which is
     * what it did while the element lived in the theme's markup above the
     * result, where it rendered in every state including the gaps.
     *
     * The element upgrades on insertion and its `disconnectedCallback` clears
     * the outgoing one's timer, so a repaint per chip click costs nothing.
     */
    /** The shield and the one-line guarantee, under Add to basket. */
    assuranceRow() {
      if (!this.assurance) return '';
      return (
        `<p class="af-buy-assure">${SHIELD}` +
        `<span>${escapeHtml(this.assurance)}</span></p>`
      );
    }

    dispatchRow() {
      const d = this.dispatch;
      if (!d?.cutoff) return '';
      return (
        `<airflux-dispatch class="af-dispatch" cutoff="${escapeAttr(d.cutoff)}"` +
        ` working-days="${escapeAttr(d.days || '1,2,3,4,5')}"` +
        ` timezone="Europe/London" today-text="for dispatch today" next-text="Next dispatch">` +
        // No dot — see `airflux-dispatch.liquid`, which this mirrors.
        `<span data-dispatch-out>${escapeHtml(
          d.fallback || 'Dispatched from the UK on the day you order, Monday to Friday.'
        )}</span>` +
        `</airflux-dispatch>`
      );
    }

    /**
     * The preference chips — Type (in/out channel), Set (2 vs 4 piece).
     *
     * 🔴 They belong ABOVE the price, and that is not the usual order. A variant
     * picker normally sits under the price because on most catalogues the
     * variant does not change it. Here it does: the Ford Kuga is £34.90
     * in-channel and £44.90 out-channel, a £10 swing on the chip directly below
     * the figure it rewrites. Read top-down, the customer met a price, then the
     * control that invalidates it.
     *
     * Above the price the same read is: what you are choosing, what it costs,
     * whether it is in stock, buy. `[data-airflux-options]` is markup outside
     * the result, so it survives the repaint each chip click triggers.
     *
     * The click handler is delegated on the host element, not on the result, so
     * chips keep working wherever they land — including the closing CTA's panel,
     * which has no slot and takes them inline as before.
     */
    optionChips(html) {
      if (!html) return '';
      if (this.els.options) {
        this._optionsHtml = html;
        return '';
      }
      return html;
    }

    /** "Full details for this set →" — the link, wherever it ends up. */
    moreLink(cur) {
      return (
        `<p class="af-resolved__more">` +
        `<a href="/products/${escapeAttr(cur.p.handle)}">Full details for this ` +
        `<span class="af-nowrap">set${ARROW}</span></a></p>`
      );
    }

    /**
     * Every car a given product fits, named, deduped, in index order.
     *
     * The index links the other way — a vehicle lists its product indices — so
     * this walks the 534 vehicles once per resolve. Cheap, and cheaper than
     * shipping a reverse map in the payload for something 524 of 642 products
     * do not need: they fit exactly one car.
     *
     * 🔴 Each name carries ITS OWN year range (2026-08-16) — "Ford Kuga
     * 2020–2026" — matching `fit_names` in `airflux-pdp-fitment.liquid`, which
     * builds the same row server-side. Per car and not once for the whole list:
     * the rebadged twins do not always share a range, and one range appended to
     * a list of five would claim four cars nobody checked.
     *
     * The dedupe was BY NAME, because two year rows of the same car are two
     * vehicles with one name — the Fiat 500 appears twice in its own five, and
     * the list read "500, 595, 695, 500, 500C". With the years in the key those
     * two are no longer one string, so both survive and the reader gets the
     * distinction the collapse was hiding. A genuine repeat still collapses.
     */
    carsForProduct(handle) {
      if (!this.index) return [];
      const idx = this.index.products.findIndex((p) => p.h === handle);
      if (idx < 0) return [];
      const names = new Set();
      for (const veh of this.index.vehicles) {
        if ((veh.d ?? []).includes(idx) || (veh.w ?? []).includes(idx)) {
          names.add(`${this.index.makes[veh.m]} ${veh.n} ${veh.f}–${veh.t}`);
        }
      }
      return [...names];
    }

    /**
     * The resolved product's own name and spec, into the first accordion.
     *
     * A landing page that has resolved IS a product page, and the one thing it
     * was still missing was the reference material a real one carries — the
     * whole catalogue title and the Fits / Type / In the box table. Same markup
     * and same classes as `airflux-pdp-fitment`'s `layout: 'spec'`, so the two
     * pages fold open to the same thing.
     *
     * 🔴 Every value here comes from the index entry (`c` channel, `k` pack) or
     * from the vehicle, which is exactly what the server-rendered table reads
     * from. Nothing is composed from the title, because the title is the string
     * this table exists so nobody has to parse.
     */
    paintDetails(cur, v, chosen) {
      if (!this.els.details) return;
      if (!cur) {
        this.els.details.innerHTML = '';
        return;
      }

      const rows = [];
      const body = cur.c.b || null;
      // EVERY car this exact part fits, not only the one resolved — the same
      // list the product page's table prints, for the same reason: a Citroën
      // Berlingo deflector is also the Peugeot Partner's, and a Partner owner
      // should read that on the part rather than infer it.
      const cars = this.carsForProduct(cur.c.h);
      // The fallback is dated too, so the row never loses its years just
      // because the reverse walk found nothing to name.
      const named = cars.length ? cars.join(', ') : this.carLabelDated(v);
      const fits = [named, cur.c.g && cur.c.g !== chosen ? cur.c.g : null, body].filter(Boolean);
      if (fits.length) rows.push(['Fits', fits.join(' · ')]);

      if (cur.c.c && cur.c.t !== 'w') {
        rows.push([
          'Type',
          cur.c.c === 'out-channel'
            ? 'Out-Channel — mounts to the outside of the door frame'
            : 'In-Channel — sits inside the window channel',
        ]);
      }
      if (cur.c.k) {
        rows.push(['In the box', `${cur.c.k} ${cur.c.t === 'w' ? 'wiper blades' : 'deflectors'}`]);
      }

      this.els.details.innerHTML =
        (cur.p.title ? `<p class="af-pdp__title-full">${escapeHtml(cur.p.title)}</p>` : '') +
        (rows.length
          ? `<dl class="af-spec">` +
            rows
              .map(
                ([k, val]) =>
                  `<div class="af-spec__row"><dt class="af-spec__key">${escapeHtml(k)}</dt>` +
                  `<dd class="af-spec__val">${escapeHtml(val)}</dd></div>`
              )
              .join('') +
            `</dl>`
          : '');
    }

    dependsNote(count) {
      if (!count) return '';
      return (
        `<p class="af-resolved__depends">` +
        `${count === 1 ? 'One more set' : `${count} more sets`} for this car depend${count === 1 ? 's' : ''} on the version. ` +
        `Your V5C logbook names it, and it is usually on a plate inside the driver's door frame.</p>`
      );
    }

    /** The attributes `addToCart` reads. Kept identical to the old card's. */
    addAttrs(s, v) {
      const variant = s.p.variants?.[0];
      return (
        `data-airflux-add data-variant="${variant?.id}" data-vehicle="${escapeAttr(v.h)}"` +
        (s.c.c ? ` data-channel="${escapeAttr(s.c.c)}"` : '') +
        (s.c.k ? ` data-pack="${s.c.k}"` : '') +
        (s.c.b ? ` data-body="${escapeAttr(s.c.b)}"` : '') +
        (s.c.g ? ` data-generation="${escapeAttr(s.c.g)}"` : '')
      );
    }

    /**
     * "We can't confirm which version you have" — the honest end of "Not sure".
     *
     * Reached where no product covers every version of this car, which after the
     * universality fix is nearly always: exactly 1 deflector car and 1 wiper car
     * in the whole catalogue have a part that genuinely fits the lot. So this is
     * what "Not sure" mostly does now, and it has to be useful rather than a
     * shrug — the V5C tells them the answer, the counterpart is something they
     * CAN buy today, and the form files the question if neither helps.
     */
    unsureGap(v) {
      track('coverage_gap_hit', { vehicle: v.h, scope: this.scope, via: 'unsure' });
      this.reportCar(v);
      // Inner html only — see the note on `versionGap` for why the panel and
      // the counterpart card both belong to the caller.
      return (
        `<p class="af-gap__msg"><strong>We'd rather check than sell you the wrong part.</strong></p>` +
        `<p class="af-gap__sub">Which version of the ${escapeHtml(this.carShort(v))} you have changes which set fits. ` +
        `Your V5C logbook names it, and it is usually on a plate inside the driver's door frame — ` +
        `or leave your email and we'll confirm it for you.</p>` +
        // The reg, not the version — they have just told us they don't know the
        // version, and asking again would be the same dead end with a text box
        // in it. A registration is something every owner can read off the front
        // of their own car, and it is the one answer that lets the client settle
        // the question outright instead of writing back to ask.
        `<div class="af-gap__capture" data-airflux-gap-slot data-car="${escapeAttr(`${this.carLabelWithYears(v)} (version unknown)`)}" data-car-key="${escapeAttr(this.carKeyOf(v))}"${this.coverageAttrs(v)}` +
        ` data-ask-label="Your reg, if you'd like us to check it for you" data-ask-placeholder="AB12 CDE"` +
        ` data-want="${escapeAttr(this.wantScope())}"></div>`
      );
    }

    /**
     * The counterpart category, as the SAME card the product page shows.
     *
     * 🔴 It used to be a line of text. That is enough beside a buy button, but
     * it is not enough when it is the only thing on the page a customer can act
     * on — which is every gap state — and it made the two surfaces describe the
     * same offer in two different ways. It now reuses `.airflux-xsell__*`
     * wholesale, so "here's the thing we DO have" looks identical whether you
     * reached it from a product page, a version we don't stock, or a car we
     * only do wipers for.
     *
     * Rendered as an empty slot and filled by `mountCrossSell`, because the
     * photograph and the live price are a fetch away and the rest of the column
     * must not wait for them.
     *
     * Silent when there is no counterpart — UX §6 is explicit that the answer to
     * "nothing fits" is silence, not a consolation offer. Only 91 of 534 cars
     * carry both categories.
     */
    crossSell(v, inline) {
      if (!this.counterparts(v).length) return '';
      // Default: on a surface that HAS a slot below the folds, the card goes
      // there. Recording the vehicle here and letting `withXsell` flush it is
      // what makes that true without each of the twelve result states having to
      // remember it.
      //
      // 🔴 `inline: true` is the ONE exception, and it is a real one. In the
      // four gap states the customer cannot buy what they came for at all, so
      // the counterpart is not a cross-sell — it is the only offer on the page.
      // Below three collapsed folds it is ~290px from the sentence that creates
      // the need for it, which is where a consolation offer goes to be missed.
      // Every gap state passes it; nothing else does.
      if (this.els.xsell && !inline) {
        this._xsellFor = v;
        return '';
      }
      // Inline: the slot goes into the result markup, so it does not exist yet
      // — `withXsell` mounts it a microtask after the assignment. Recorded here
      // rather than mounted by each caller, for the same reason the below-folds
      // path is: twelve result states, and per-site mounting is what left the
      // "Mine isn't listed here" card unmounted the first time this moved.
      this._xsellInline = v;
      return `<div data-airflux-xsell></div>`;
    }

    /**
     * Every write to `[data-airflux-result]` passes through here, and that is
     * the point. Two things the engine draws live OUTSIDE that element — the
     * counterpart card below the folds and the option chips above the price —
     * so without a single choke point a state that paints neither leaves the
     * previous state's stranded on the page.
     *
     * A state that wants one calls `crossSell()` while building its markup,
     * which sets `_xsellFor`; a state that does not, does not, and the slot is
     * cleared. Arguments evaluate before the call, so the flag is always set by
     * the time this runs.
     */
    withXsell(html) {
      if (this.els.xsell) {
        const v = this._xsellFor;
        this._xsellFor = null;
        this.els.xsell.innerHTML = '';
        if (v) this.mountCrossSell(v, this.els.xsell);
      }
      // Same lockstep for the option chips: a state that paints none leaves
      // `_optionsHtml` null and the slot is emptied, so a previous state's
      // chips can never outlive it above the price.
      if (this.els.options) {
        this.els.options.innerHTML = this._optionsHtml || '';
        this._optionsHtml = null;
      }
      // The inline slot is part of `html` and does not exist until the caller
      // has assigned it, so its mount waits one microtask.
      //
      // ⚠️ Two different elements, two different attributes, on purpose:
      // `[data-airflux-xsell-slot]` is the permanent container below the folds,
      // `[data-airflux-xsell]` is the throwaway slot a gap state paints into the
      // result. They shared a name until 2026-08-15 and it worked only because
      // the result is empty when `els` is read at connect — one refactor away
      // from binding to the wrong element with no error.
      const inlineFor = this._xsellInline;
      this._xsellInline = null;
      if (inlineFor) queueMicrotask(() => this.mountCrossSell(inlineFor));
      return html;
    }

    /**
     * What we stock in the OTHER category for this car, deduped exactly as the
     * main list is — one card per genuinely different thing to buy.
     */
    counterparts(v) {
      if (this.scope === 'all' || !v) return [];
      // Narrowed to the version the customer has answered — the same `vo[].p`
      // the main list is filtered by, so the two can never disagree: an A3
      // resolved as the Saloon is not offered the Convertible's blade under
      // its deflectors (2026-09-04). No answer, or "Not sure", and the row
      // stands as it is.
      //
      // ⚠️ Only THIS row's answer. The labels are generic — "4-door Saloon" is
      // an option on the A3 and on the A4 — so a version remembered while
      // another car was on the page must never narrow this one. It is this
      // widget's when the row is the one it rendered, and the stored car's
      // when the row is the stored car (the scope gap reaches here without a
      // render); anything else is no answer.
      const car = Car.get();
      const version = this._vehicle?.h === v.h ? this._version : car?.handle === v.h ? car.version : null;
      const row = window.AirfluxXsell.forVersion(v, version ?? null);
      // Shared with the cart drawer since 2026-08-16 — see `airflux-xsell.js`
      // for the dedupe rule. The comparator stays local because this surface has
      // a vehicle in hand and can break ties on whether the kit names the
      // customer's own generation; the cart has no such context and takes the
      // module's newest-then-in-stock-then-cheapest default.
      return window.AirfluxXsell.counterparts(this.index, row, this.scope === 'wipers' ? 'd' : 'w', (c, prev) =>
        this.preferred({ c, p: { available: c.a, price: c.p } }, { c: prev, p: { available: prev.a, price: prev.p } })
      );
    }

    async mountCrossSell(v, target) {
      // 🔴 The target is explicit because the card lands in two different
      // places. On a RESOLVED landing page it goes to `[data-airflux-xsell]`
      // below the folds — it is a second thing to buy, and it belongs after the
      // reference material rather than between the button and it. In a GAP
      // state it stays inline in the result, because there it is the only thing
      // on the page the customer can act on and burying it under three closed
      // accordions would be the consolation-offer version of silence.
      const slot = target ?? this.els.result.querySelector('[data-airflux-xsell]');
      if (!slot || slot.childElementCount) return;
      const cands = this.counterparts(v);
      if (!cands.length) return;

      const live = await fetchProducts(cands.map((c) => c.h));
      const byHandle = new Map(live.map((p) => [p.handle, p]));
      const rows = cands.map((c) => ({ c, p: byHandle.get(c.h) })).filter((r) => r.p);
      if (!rows.length || !slot.isConnected) return;

      const other = this.scope === 'wipers' ? 'd' : 'w';
      slot.innerHTML =
        `<div class="airflux-xsell">` +
        `<h3 class="airflux-xsell__title">${escapeHtml(ROUTE[other].noun)} for the same car</h3>` +
        `<ul class="airflux-xsell__list" role="list">${rows.map((r) => this.crossSellItem(r, v)).join('')}</ul>` +
        `</div>`;
      track('crosssell_shown', { vehicle: v.h, scope: this.scope, options: rows.length });
      this.recordHeights();
    }

    /**
     * The card itself is `airflux-xsell.js`, shared with the cart drawer since
     * 2026-08-16. Only the Add button's attributes are this surface's own — here
     * the engine owns adding, in the drawer `airflux-cart.js` does — so that is
     * all that is passed in.
     *
     * The car is not named on this surface: the page has already resolved to one
     * car and every card on it is for that car, so a label would be the same
     * words under each. The drawer names it, because a basket can hold parts for
     * more than one.
     */
    crossSellItem({ c, p }, v) {
      return window.AirfluxXsell.card({ c, p, attrs: this.addAttrs({ c, p }, v) });
    }

    /** Make and model only — the line already names the car once. */
    carShort(v) {
      return `${this.index.makes[v.m]} ${v.n}`;
    }

    // ── Shopify's own express checkout, for a client-side resolved product ───

    /**
     * Fetches the buy form for one product and drops it in beside Add to cart.
     *
     * `/products/{handle}?section_id=airflux-buy-box` renders a section that is
     * on no template at all, in that product's Liquid context. Verified on the
     * dev store: the `<shopify-accelerated-checkout>` it returns upgrades into a
     * live "Buy it now" once injected here, because `portable-wallets.en.js` is
     * loaded on every page of the storefront rather than only on product ones.
     *
     * 🔴 The line-item properties are written into that form before it can be
     * used, and they have to be: an express checkout never passes through the
     * cart, so the cart attributes `Car.set` writes cannot reach the order. This
     * is the only place the customer's car can be attached to an express buy.
     *
     * Everything here is best-effort. A failed fetch leaves Add to cart doing
     * exactly what it does today — the page is never blocked on this.
     */
    async mountExpress(entry, v) {
      const slot = this.els.result.querySelector('[data-airflux-express]');
      if (!slot) return;
      const handle = entry.p.handle;

      const html = await this.buyBoxHtml(handle);
      // A chip click during the fetch changes the answer — never paint a buy
      // button for a product the page has moved on from.
      if (!html || this._current?.p.handle !== handle || !slot.isConnected) return;

      slot.innerHTML = html;
      const props = slot.querySelector('[data-af-buybox-props]');
      if (props) {
        const car = Car.get();
        const values = window.AirfluxOrder.carProperties(
          { ...(car ?? {}), handle: v.h, make: this.index.makes[v.m], model: v.n, years: `${v.f}–${v.t}`, body: car?.body || v.b || null },
          { channel: entry.c.c, pack: entry.c.k }
        );
        props.innerHTML = Object.entries(values)
          .map(([k, val]) => `<input type="hidden" name="properties[${escapeAttr(k)}]" value="${escapeAttr(val)}">`)
          .join('');
      }
      this.paintStock(slot.querySelector('[data-af-buybox]'), handle);
      track('express_checkout_shown', { vehicle: v.h, product: handle });

      this.recordHeights();

      // Warm the other options so a chip click repaints without a round trip.
      for (const s of this._visibleForAxes ?? []) if (s.p.handle !== handle) this.buyBoxHtml(s.p.handle);
    }

    /**
     * "Only 2 left" — the real count, once the buy box has brought it.
     *
     * 🔴 The landing page cannot know this at paint time and must not pretend
     * to. `/products/{handle}.js`, which prices every option on this page,
     * reports `available` and nothing about how many: Shopify withholds
     * `inventory_quantity` from that endpoint. So the stock row opens at the
     * only thing the index can honestly say — "In stock" — and this upgrades it
     * a moment later, when `airflux-buy-box` lands with the figure from Liquid.
     *
     * That order is deliberate and it only ever moves one way. The opening
     * claim is true; the replacement is the same claim said more precisely. The
     * reverse — holding the row blank until the fetch returns — would cost
     * every customer a beat of nothing on the one line that qualifies the
     * button, to spare the few on a low-stock product a word changing.
     *
     * ⚠️ Guarded on the handle for the same reason `mountExpress` is: the fetch
     * is per product and a chip click during it changes which product the page
     * is about. A count from the 2-piece set must never land on the 4-piece.
     *
     * The threshold, the tracking rules and the sentence itself are all decided
     * in the section — see `airflux-buy-box.liquid`. Nothing here knows what
     * "low" means, which is what keeps this page and the real product page from
     * disagreeing about the same variant.
     */
    paintStock(box, handle) {
      if (!box || this._current?.p.handle !== handle) return;
      if (box.dataset.stockLow !== '1' || !box.dataset.stockLabel) return;
      const line = this.els.result?.querySelector('.af-pdp__stock');
      if (!line) return;
      line.textContent = box.dataset.stockLabel;
      line.classList.add('af-pdp__stock--low');
    }

    buyBoxHtml(handle) {
      if (!this._buyBoxes.has(handle)) {
        this._buyBoxes.set(
          handle,
          fetch(`/products/${encodeURIComponent(handle)}?section_id=airflux-buy-box`)
            .then((r) => (r.ok ? r.text() : null))
            .catch(() => null)
        );
      }
      return this._buyBoxes.get(handle);
    }

    /**
     * Writes the chosen version onto the stored car.
     *
     * 🔴 Without this the choice never left the widget. A customer could pick
     * "E90 · 4-door Saloon", click through to a product, and the page would
     * greet them with "✓ Fits your BMW 3 Series" — true of the car, but the car
     * has four versions and that deflector fits one. The product page cannot
     * check a version it was never told.
     */
    rememberVersion() {
      const car = Car.get();
      if (!car?.handle || car.handle !== this._vehicle?.h) return;
      const opt = (this._vehicle.vo ?? []).find((o) => o.l === this._version);
      this.setCar({
        ...car,
        version: this._version ?? null,
        versionCode: opt?.c ?? null,
        versionBody: opt?.b ?? null,
        label: `${this.label(this._vehicle)}${car.year ? ` ${car.year}` : ''}`,
      });
    }

    /**
     * The version question, asked only where the answer changes what you get.
     *
     * A "version" is a chassis code where the listings name them (E90 / E91),
     * a body where they name those instead (Estate / 5-door Hatchback), or both
     * where we have both ("F21 · 3-door Hatchback"). Whichever it is, the
     * options and their products are precomputed in the index, so a product
     * covering every version is already listed under all of them: pick any
     * version of a 3 Series 2005–2012 and the same wiper appears, while the
     * deflector only shows for the E90.
     *
     * "Not sure" sits alongside from the very start, never only after a choice —
     * an owner who cannot answer must not be stuck.
     */
    versionChooser(v, options = this.versionQuestion(v), escape = '') {
      if (!options.length) return '';
      const chosen = this._version;
      const unsure = this._unsure === true;
      // Same chip language as the preference axes, with `--ask` marking it as
      // the one the customer has to get RIGHT rather than the one they get to
      // prefer. Only 34 of 534 cars ever see it.
      return (
        `<div class="af-opt af-opt--ask">` +
        `<p class="af-opt__label">Which one is yours?` +
        ` <button type="button" class="af-opt__help-btn" data-airflux-helper>${INFO}` +
        `<span class="af-visually-hidden">How do I tell?</span></button></p>` +
        `<div class="af-opt__chips" role="group" aria-label="Which version is yours?">` +
        options
          .map(
            (o) =>
              `<button type="button" class="af-chip${o.l === chosen ? ' is-selected' : ''}"` +
              ` data-airflux-version="${escapeAttr(o.l)}" aria-pressed="${o.l === chosen}">` +
              (o.c ? `<span class="af-chip__code">${escapeHtml(o.c)}</span>${o.b ? ' · ' : ''}` : '') +
              `${escapeHtml(o.b ?? (o.c ? '' : o.l))}</button>`
          )
          .join('') +
        `<button type="button" class="af-chip af-chip--quiet${unsure ? ' is-selected' : ''}" data-airflux-unsure aria-pressed="${unsure}">Not sure</button>` +
        `</div>` +
        `<p class="af-opt__help" hidden>Count the doors, boot included — a 5-door hatchback has a tailgate, an estate has a long roof over the boot. Where a code like F20 is shown, it is on your V5C logbook and usually on a plate in the door frame. If yours is not listed, tell us and we'll check.</p>` +
        // 🔴 INSIDE the box, not under it. "Mine isn't listed here" is the last
        // option in the same question the chips ask — pick a version, or say
        // none of them is yours — and sitting outside the panel it read as an
        // unrelated link that happened to follow. The two answers now look
        // like two answers.
        //
        // ⚠️ It needs NO margin of its own. `.af-opt--ask` is a flex column
        // with a row gap, so it is already spaced exactly as the chips are
        // from the label above them — a margin here does not replace that gap,
        // it adds to it, and the link drifted twice as far from the chips it
        // belongs to as the chips are from their own heading.
        escape +
        `</div>`
      );
    }

    /**
     * The escape hatch, on every resolved car.
     *
     * The catalogue can only describe versions its titles mention. A BMW 1
     * Series row covers the 5-door F20 and the 3-door F21, but only the F20 is
     * ever written down — so a 3-door owner has no option to pick and no way to
     * say so. Rather than let them guess at a kit that may not fit, this turns
     * the dead end into a demand signal the panel can act on.
     */
    notMyCar(v) {
      // 🔴 Only where there is something that COULD be unlisted: a car with
      // version options, or a row whose chassis codes span more than one
      // generation. On the other 489 of 534 cars the row is a single version
      // that every product for it fits, so "mine isn't listed here" answers a
      // question nobody has — and it was the third quiet underlined link in a
      // row at the foot of the buy column, which is where a reader stops
      // reading them. Those customers still have "Not your car? Change it".
      const spansVersions = String(v?.g || '').split(/[\s,]+/).filter(Boolean).length > 1;
      if (!v?.vo?.length && !spansVersions) return '';
      return (
        `<p class="af-notmine">` +
        `<button type="button" class="af-notmine__btn" data-airflux-notmine>Mine isn't listed here</button>` +
        `</p>`
      );
    }

    /**
     * Does this product cover LESS of the selected car than the row promises?
     *
     * Pure comparison of two lists of codes that are already written on the
     * products — the row says "F20 F21" because the titles between them do; this
     * kit says "F20". Nothing is looked up and nothing is assumed: we never
     * claim to know what an F20 *is*, only that this one covers fewer of the
     * versions than the row spans.
     */
    partialCover(c) {
      const span = String(this._vehicle?.g || '').split(/[\s,]+/).filter(Boolean);
      const mine = String(c.g || '').split(/[\s,]+/).filter(Boolean);
      if (span.length < 2 || !mine.length) return false;
      return span.some((code) => !mine.includes(code));
    }

    /**
     * Which of two genuinely interchangeable products to show (§3.2a).
     *
     * 🔴 NEWEST UPLOAD FIRST — ahead of stock and ahead of price, changed
     * 2026-09-05 at the client's instruction. A duplicate is the same part
     * listed twice, and the later listing is the one they meant: the Dacia
     * Bigster was re-uploaded on 2026-09-04 covering 2025–2027 at £44.90 while
     * the 2026-01-24 original covering 2025–2026 sat at £35.90, so the cheapest
     * rule kept showing the superseded years. Price cannot express that; the
     * upload date can.
     *
     * Then in stock, then cheapest — then the listing whose title actually
     * names this car's version. That last step is not cosmetic: the Citroën
     * Berlingo has an in-channel 2-piece titled "BERLINGO MK3" and another
     * titled "BERLINGO, Fits PARTNER, RIFTER, COMBO, PROACE CITY", both £34.90.
     * Whichever wins is what the customer sees and clicks through to, and the
     * one naming their generation is the clearer page to land on. Left to array
     * order it was arbitrary, and the MK3 one lost.
     *
     * ⚠️ Newest ahead of in-stock is a stated trade: where a re-upload is out
     * of stock and the listing it replaced is not, the page now shows the
     * unbuyable one. It cost nothing on the catalogue as it stands (0 of 68
     * duplicate sets, measured 2026-09-05) because a re-upload is normally the
     * live listing — but it is the risk the order buys, and the reason the
     * clause below it is stock rather than price.
     *
     * `u` is a DAY, so two listings from the same bulk upload tie here and fall
     * through to stock and price exactly as they did before — see index-core.
     * A listing with no `u` at all sorts oldest, never newest: an unknown date
     * must not win a contest it cannot evidence.
     *
     * Falls back to the handle so the result never depends on ordering.
     */
    /**
     * Candidates → exactly what the page shows, in the order it shows it.
     *
     * `byHandle` maps a handle to something carrying `available` and `price`.
     * That is deliberately the ONLY thing this needs, because it is called
     * twice with two different sources:
     *
     *   - with live `/products/{h}.js` payloads, to paint the real result;
     *   - with the index's own `{ available: c.a, price: c.p }`, to work out
     *     which buy box to warm BEFORE those payloads arrive (see `render`).
     *
     * 🔴 It exists as one method so those two passes can never disagree. They
     * were the same logic written once and called twice from the start; the
     * moment it is inlined again, a rule added to one ranking and not the other
     * shows the customer a buy box for a product the page is not selling.
     */
    rankCandidates(candidates, byHandle) {
      // Collapse genuine duplicates to one card: newest upload day first, then
      // in stock, then cheapest (§3.2a). Never render two products a customer
      // can't tell apart.
      //
      // 🔴 The key includes BODY. A vehicle no longer forks by body, so a Focus
      // hatchback kit and a Focus estate kit now sit on one car — and on a
      // channel-only key one of them was hidden as a "duplicate". Measured on
      // the live catalogue: 18 buckets held products with genuinely different
      // bodies, including three-way splits on the Focus and the Corolla. Hiding
      // one of those sells someone an estate kit for a hatchback.
      //
      // It also reads the category from the INDEX (`c.t`) rather than the live
      // `product.type` string, so a product type renamed in Admin can't quietly
      // stop duplicates collapsing.
      const shown = [];
      const seen = new Map();
      for (const c of candidates) {
        const p = byHandle.get(c.h);
        if (!p) continue;
        // ⚠️ Generation is NOT part of this key, deliberately. By the time cards
        // render, everything visible already covers the selected version — so
        // two products differing only in whether their title happened to name a
        // code are, to this customer, the same thing. Including it split the
        // Citroën Berlingo MK3 into two in-channel cards at the identical price:
        // one titled "BERLINGO MK3", one titled "BERLINGO, Fits PARTNER,
        // RIFTER…". Both fit, neither is distinguishable, and §3.2a is explicit
        // that we never render two cards a customer cannot tell apart.
        //
        // 🔴 PACK SIZE IS. §3.2a assumes "pack size is never a choice — it is
        // fixed by the car", and that is not true of this catalogue: the
        // Berlingo, the Doblo and the Rifter each stock a 2-piece set for the
        // front windows AND a 4-piece for all four. Those are different things
        // to buy at different prices, and leaving pack out of the key silently
        // hid the 4-piece behind the cheaper 2-piece.
        const key = `${c.t ?? 'x'}|${c.c ?? 'x'}|${c.b ?? 'any'}|${c.k ?? 'x'}`;
        const prev = seen.get(key);
        const better = !prev || this.preferred({ c, p }, prev);
        if (better) seen.set(key, { c, p });
      }
      for (const { c, p } of seen.values()) shown.push({ c, p });
      // Deflectors first, then wipers; cheapest first within a type — and then
      // fully determined, because the FIRST entry is now the option the page
      // opens on rather than one card among several.
      //
      // 🔴 The Berlingo's in-channel and out-channel sets are both £34.90, so
      // the old two-key sort left the default to Map insertion order, which is
      // index order, which is arbitrary. In-channel wins the tie deliberately:
      // it is 390 of the 478 deflector listings, so it is the likelier answer —
      // and any stated rule beats a default that can change when the catalogue
      // is re-synced.
      const CHANNEL_RANK = { 'in-channel': 0, 'out-channel': 1 };
      shown.sort(
        (a, b) =>
          String(a.c.t).localeCompare(String(b.c.t)) ||
          a.p.price - b.p.price ||
          (CHANNEL_RANK[a.c.c] ?? 2) - (CHANNEL_RANK[b.c.c] ?? 2) ||
          (a.c.k ?? 0) - (b.c.k ?? 0) ||
          String(a.c.h).localeCompare(String(b.c.h))
      );
      return shown;
    }

    /**
     * The listings in `ids` a price may be quoted from — duplicates collapsed
     * exactly as the cards collapse them, in stock preferred, cheapest first.
     * `[0]` is therefore the "from" price and the product to link to.
     *
     * 🔴 Every "from £X" on this page has to come off this, never off the raw
     * index list. Until 2026-09-05 the two were the same thing: the collapse
     * kept the cheapest, so the cheapest listing was always the one on offer.
     * The rule now keeps the NEWEST upload, so the cheapest listing in the raw
     * list can be one the store never shows — and quoting it is a price promise
     * the landing page then breaks. Found in the browser on the Dacia Bigster:
     * the home page card read "from £35.90" and the page it linked to charged
     * £44.90, because the £35.90 listing is the superseded one.
     *
     * `ids` is always ONE category, which is why the row handed to
     * `counterparts` can set both `d` and `w` to it — that function reads only
     * whichever the `t` argument names, and keys the collapse on channel, body
     * and pack. Falls back to the raw list if the cross-sell module never
     * loaded: the pre-collapse behaviour, rather than an empty row.
     *
     * ⚠️ The generation tiebreak inside `preferred` reads `this._vehicle`, not
     * anything passed here — on the coverage-gap path no vehicle is current, so
     * that clause simply never fires and the handle decides. Deterministic
     * either way; it is not reading the wrong car's codes.
     */
    quotable(ids, t) {
      const pool = (ids ?? []).map((i) => this.index.products[i]).filter(Boolean);
      const collapsed = window.AirfluxXsell?.counterparts(this.index, { d: ids, w: ids }, t, (c, prev) =>
        this.preferred({ c, p: { available: c.a, price: c.p } }, { c: prev, p: { available: prev.a, price: prev.p } })
      );
      const reachable = collapsed?.length ? collapsed : pool;
      const sellable = reachable.filter((p) => p.a);
      return [...(sellable.length ? sellable : reachable)].sort((a, b) => a.p - b.p);
    }

    preferred(a, b) {
      // The upload day comes off the INDEX record (`c.u`), never off the live
      // `/products/{h}.js` payload — that payload has no created date, and this
      // method is called with both sources (see `rankCandidates`). Reading it
      // from the index keeps the two passes agreeing, which is the whole reason
      // one method serves both.
      const day = (x) => String(x.c?.u ?? '');
      if (day(a) !== day(b)) return day(a) > day(b);
      if (!!a.p.available !== !!b.p.available) return !!a.p.available;
      if (a.p.price !== b.p.price) return a.p.price < b.p.price;
      const rowCodes = String(this._vehicle?.g || '').split(/[\s,]+/).filter(Boolean);
      const names = (x) => String(x.c.g || '').split(/[\s,]+/).filter(Boolean).some((g) => rowCodes.includes(g));
      if (names(a) !== names(b)) return names(a);
      return String(a.c.h).localeCompare(String(b.c.h)) < 0;
    }

    async addToCart(button) {
      const car = Car.get();
      const v = this.index.vehicles.find((x) => x.h === button.dataset.vehicle);
      button.disabled = true;
      // The label lives in a span on the theme's pill (`.af-btn__label`), so
      // writing to `textContent` would delete the element the next state has to
      // be written back into.
      const label = button.querySelector('.af-btn__label') ?? button;
      const original = label.textContent;
      label.textContent = 'Adding…';

      // Line-item properties so the client can sanity-check the pick (§5.3.6).
      // Derived by the shared module — see assets/airflux-order.js for why the
      // year is never inferred from the range and why Generation is always the
      // customer's answer rather than the part's coverage.
      //
      // Channel and Pack still come off the PRODUCT: the same Kuga has an
      // In-Channel and an Out-Channel set, and the slip must say which was
      // bought. Body likewise falls back to the button's product body.
      const properties = v
        ? window.AirfluxOrder.carProperties(
            {
              ...(car ?? {}),
              handle: v.h,
              make: this.index.makes[v.m],
              model: v.n,
              years: `${v.f}–${v.t}`,
              // The car's body only as a last resort — `versionBody` (spread in
              // above) is the customer's own answer and outranks it.
              body: car?.body || v.b || null,
            },
            { channel: button.dataset.channel, pack: button.dataset.pack }
          )
        : {};

      // The cart Dawn will repaint from this response, if the theme has one.
      // Without asking for its sections the add succeeded and NOTHING on the
      // page moved — the basket count only caught up on the next navigation,
      // so a customer who had just pressed the only button on the page was
      // shown an apparently empty basket. Dawn's own product form has always
      // sent these; the widget never did.
      const cart = document.querySelector('cart-notification') ?? document.querySelector('cart-drawer');
      const sections = cart?.getSectionsToRender?.().map((s) => s.id) ?? [];

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(button.dataset.variant), quantity: 1, properties }],
            ...(sections.length ? { sections, sections_url: window.location.pathname } : {}),
          }),
        });

        if (!res.ok) {
          // The cached index can be stale; the add response is the truth (§8).
          const err = await res.json().catch(() => ({}));
          label.textContent = /sold out|unavailable|stock/i.test(err.description || err.message || '')
            ? 'Out of stock'
            : 'Unavailable';
          track('out_of_stock_shown', { vehicle: v?.h });
          return;
        }

        // Hand the rendered sections to the cart so the basket count and the
        // drawer are correct before the customer looks at them. `renderContents`
        // opens the drawer as its last step, which is the confirmation this
        // path has never given — the button's own "Added ✓" is a label on a
        // page that otherwise looks unchanged.
        if (cart && sections.length) {
          // 🔴 `renderContents` does NOT clear the drawer's own empty state —
          // it looks for `is-empty` on `.drawer__inner`, while the markup puts
          // it on `<cart-drawer>` itself. Dawn's product form carries the fix
          // (product-form.js:115) rather than the drawer, so every other
          // add-to-cart path has to repeat it. Without this the drawer slides
          // open on `cart-drawer.is-empty .drawer__inner`, which is a rule that
          // hides the line items — an open, apparently empty basket a second
          // after the customer added something to it.
          cart.classList.remove('is-empty');
          cart.setActiveElement?.(button);
          try {
            cart.renderContents(await res.clone().json());
          } catch {
            /* the add worked; a repaint that didn't is not worth failing over */
          }
        }

        // Source-aware (§11: conversion split by entry path). The hero layout
        // is a landing page; otherwise it's the homepage/change-car widget.
        const source = this.layout === 'hero' ? 'add_to_cart_from_landing' : 'add_to_cart_from_widget';
        track(source, { vehicle: v?.h, car: car?.label });
        // A channel choice was actually made only when two options were offered.
        if (button.dataset.channel && this._optionCount > 1)
          track('channel_selected', { vehicle: v?.h, channel: button.dataset.channel });
        document.dispatchEvent(new CustomEvent('airflux:added-to-cart', { bubbles: true }));
        label.textContent = 'Added ✓';
        setTimeout(() => {
          label.textContent = original;
          button.disabled = false;
        }, 2000);
      } catch {
        label.textContent = 'Try again';
        button.disabled = false;
      }
    }
  }

  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const escapeAttr = escapeHtml;

  // ── car demand counter (§7, §3.4) ──────────────────────────────────────────
  /**
   * Tell the Worker a car was identified, so the control panel can rank demand.
   *
   * 🔴 Added 2026-08-19. The panel's "cars we don't stock" list was built from
   * email sign-ups alone, so it counted the few who filled in a form rather than
   * everyone who wanted the car — a small and unrepresentative slice presented
   * as the whole. This fires whether or not the shopper types anything.
   *
   * Sends NO personal data: a car, and what we stock for it. Nothing else.
   *
   * Fire-and-forget with `keepalive`, so nothing the shopper sees ever waits on
   * it and it still completes if they navigate away in the same moment.
   */
  const RESOLVE_SEEN_KEY = 'af_resolved_seen';

  /**
   * A stable key for a car the catalogue does not list.
   *
   * Catalogue cars use their own vehicle handle, which cannot fork. This is the
   * fallback for a plate that resolved to something we have never stocked — the
   * exact case the demand list exists to surface. Must satisfy the Worker's
   * `^[a-z0-9][a-z0-9-]{0,79}$`, so it is trimmed to a leading alphanumeric and
   * returns '' rather than something the endpoint would reject.
   */
  const slugKey = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80)
      .replace(/-+$/, '');

  /** `both` / `one` / `none` — what we stock for this catalogue vehicle. */
  function coverageOf(v) {
    const d = v && v.d && v.d.length ? 1 : 0;
    const w = v && v.w && v.w.length ? 1 : 0;
    return d && w ? 'both' : d || w ? 'one' : 'none';
  }

  /**
   * The catalogue's verdict on a car, in the two fields the Worker stores.
   *
   * 🔴 ONE definition, shared by the demand counter and the sign-up form, for
   * the same reason `carKeyOf` is shared: the two describe the same car to the
   * same KV record, and whichever arrives first opens it. They were not shared
   * — the form sent no verdict at all — so a sign-up from a shopper `/resolve`
   * never saw (analytics refused, or the banner still unanswered) opened the
   * row as `coverage: 'none'` regardless, putting cars we already stock a line
   * for into the panel's "we stock nothing for these".
   *
   * `stocks` is the line we DO stock, and is meaningful only for `one` — the
   * panel prints it as "no wipers" / "no deflectors", which is the half of a
   * half-covered car anybody can act on.
   */
  function coverageState(v) {
    const coverage = v ? coverageOf(v) : 'none';
    return { coverage, stocks: coverage === 'one' ? (v.d && v.d.length ? 'deflectors' : 'wipers') : '' };
  }

  /**
   * Once per car per visitor per day — and once more per product line.
   *
   * The count should mean "45 people wanted a Kuga", not "45 clicks": one shopper
   * checking their car, changing it and checking back is one person, and the
   * widget makes that very easy to do. It also removes most of the duplicate
   * writes before they are sent, which matters because the counter is the only
   * unbounded writer against the free plan's 1,000 KV writes a day.
   *
   * 🔴 The obvious version of this — one entry per car — was wrong, and the live
   * page proved it. Most templates carry TWO widgets: the page's own, scoped to
   * deflectors or wipers, and an unscoped finder in the footer CTA. Both resolve
   * the same stored car, so both report it, and a car-only guard let through
   * whichever fired first. On /pages/wind-deflectors that was the UNSCOPED one,
   * so the fact that the shopper wanted *deflectors* — the whole point of
   * recording scope — was silently dropped for every visitor.
   *
   * So the guard is per car AND per car+line, and the two answers are different
   * things: `send` is whether to report at all, `countLookup` is whether this
   * report is the one that counts as a person. A second report for the same car
   * from a different-scoped widget adds its "wanted" signal without inventing a
   * second shopper.
   */
  function dedupeResolve(key, scope) {
    const today = new Date().toISOString().slice(0, 10);
    const seen = window.AirfluxStorage?.readJSON(RESOLVE_SEEN_KEY) || null;
    const state = !seen || seen.day !== today || !Array.isArray(seen.keys) ? { day: today, keys: [] } : seen;

    const scopeTag = `${key}|${scope || ''}`;
    const carSeen = state.keys.includes(key);
    const scopeSeen = state.keys.includes(scopeTag);
    if (carSeen && scopeSeen) return { send: false, countLookup: false };

    if (!carSeen) state.keys.push(key);
    if (!scopeSeen) state.keys.push(scopeTag);
    // Bounded — a single visitor cannot grow this without limit.
    if (state.keys.length > 400) state.keys = state.keys.slice(-400);
    // Private mode / storage full is swallowed by the module. Reporting twice
    // is a far smaller problem than not reporting at all, so carry on.
    window.AirfluxStorage?.writeJSON(RESOLVE_SEEN_KEY, state);
    return { send: true, countLookup: !carSeen };
  }

  /**
   * Where a report waits while nobody has been asked yet.
   *
   * 🔴 This queue exists because of a real, measurable loss, not for tidiness.
   * `airflux-consent.js` is deferred and the Customer Privacy API behind it is
   * a SECOND async load, so on a first visit the answer lands well after the
   * page is usable. The reg box is the first thing on the page and the banner
   * is a card in the corner — so the overwhelmingly common order of events is:
   * shopper types their reg, we pay the provider for the lookup, we resolve
   * their car, and only THEN do they get round to pressing "Accept all".
   *
   * Reporting straight from a `may('analytics')` check dropped that first
   * resolve on the floor for everyone, accepters included. It was the lookup
   * the shop had just paid for and, on a single-page visit, the only one there
   * was. Parking it costs nothing and `airflux:consent-changed` releases it the
   * instant the answer arrives.
   *
   * Session-only by construction: the key is deliberately NOT in the register
   * in `snippets/airflux-storage.liquid`, and `write` puts an unregistered key
   * in `sessionStorage` and nowhere else. So it never reaches the disk, never
   * outlives the tab, and a shopper who refuses has it deleted rather than
   * merely ignored.
   */
  const PENDING_RESOLVE_KEY = 'airflux:pending-resolve';

  /* Set from the consent event. Before it arrives the honest answer is "nobody
     has been asked", which is what makes parking the right default. */
  let consentDecided = false;

  function parkResolve(endpoint, payload) {
    const queue = window.AirfluxStorage?.readJSON(PENDING_RESOLVE_KEY);
    const rows = Array.isArray(queue) ? queue : [];

    /* Same car, same line, once. A shopper can resolve the same car a dozen
       times before answering the banner — reload, cross-sell, footer finder —
       and the flush must not turn that into a dozen reports. This is the same
       per-car-AND-per-car+line pair `dedupeResolve` uses, for the same reason. */
    const tag = `${payload.key}|${payload.scope || ''}`;
    if (rows.some((row) => `${row?.payload?.key}|${row?.payload?.scope || ''}` === tag)) return;

    rows.push({ endpoint, payload });
    // Bounded, like the dedupe list. Nobody resolves twenty distinct cars
    // before answering a banner, and an unbounded queue is a quota bug waiting.
    window.AirfluxStorage?.writeJSON(PENDING_RESOLVE_KEY, rows.slice(-20));
  }

  function flushPendingResolves() {
    const queue = window.AirfluxStorage?.readJSON(PENDING_RESOLVE_KEY);
    // Cleared FIRST. A send that fails must not leave the row behind to be
    // retried on every later page — the counter is not worth that.
    window.AirfluxStorage?.remove(PENDING_RESOLVE_KEY);
    if (!Array.isArray(queue)) return;
    for (const row of queue) {
      if (row && row.endpoint && row.payload && row.payload.key) sendResolve(row.endpoint, row.payload);
    }
  }

  /* The answer, whenever it arrives — on this page or three pages later, since
     both this file and the consent one are on every template. */
  document.addEventListener('airflux:consent-changed', (event) => {
    const detail = event.detail || {};
    consentDecided = !!detail.decided;
    if (detail.analytics) return flushPendingResolves();
    // Answered, and analytics was not among it: the queue is not pending any
    // more, it is refused. Deleted rather than left to rot in the tab.
    if (detail.decided) window.AirfluxStorage?.remove(PENDING_RESOLVE_KEY);
  });

  function reportResolvedCar(plateEndpoint, payload) {
    // Derived from the plate endpoint rather than carried as its own setting:
    // they are sibling routes on the same Worker by construction, so there is no
    // configuration in which one is right and the other wrong. Anchored to the
    // end so a custom path cannot be silently rewritten mid-URL.
    if (!plateEndpoint || !/\/plate$/.test(plateEndpoint)) return;
    if (!payload.key) return;

    /* 🔴 Analytics consent, and the whole report stands or falls on it.
       This is a measurement of how the shop is used, which is the one thing on
       the banner labelled "Analytics" — so a shopper who left that switch off
       has said no to exactly this. It is gated rather than merely deduped
       differently because the DUAA's new statistical-purposes exception
       (PECR Schedule A1 para 5) requires that a visitor who objects is not
       counted, and the switch IS the objection.

       `may('analytics')` is the marker `airflux-consent.js` mirrors out of
       Shopify's region-aware `analyticsProcessingAllowed()`. Outside the
       configured regions that answers true, so the demand signal is unchanged
       for visitors PECR does not reach.

       ⚠️ "Not allowed" is TWO states and they get opposite treatment. Refused
       is final and the report is dropped. Not-yet-asked is not a refusal at
       all — it is the API still loading — so the report is parked and released
       if the answer turns out to be yes. See `parkResolve` above. */
    if (!window.AirfluxStorage?.may('analytics')) {
      if (!consentDecided) parkResolve(plateEndpoint, payload);
      return;
    }

    sendResolve(plateEndpoint, payload);
  }

  /** The send itself, shared by the live path and the flush. */
  function sendResolve(plateEndpoint, payload) {
    const { send, countLookup } = dedupeResolve(payload.key, payload.scope);
    if (!send) return;
    try {
      fetch(plateEndpoint.replace(/\/plate$/, '/resolve'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, countLookup }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* a counter must never break the widget */
    }
  }

  /**
   * Clones the site-wide capture template (which carries Shopify's form token)
   * into an out-of-stock card, setting the product and car so the sign-up
   * records what the customer is waiting on.
   *
   * 🔴 It also turns on the form's free-text question where the mounting state
   * asked for one. Three states reach this file admitting they do NOT know the
   * customer's car — "mine isn't listed here", "not sure which version", and
   * the cascade having no row for the car at all — and every one of them used
   * to collect an address and nothing else. The customer record then read
   * "Asked us to cover Lexus UX (version not listed)", which says a sale was
   * lost and not one thing about what would have saved it, and no second place
   * held the answer. The question is asked here, in the form, because that is
   * the only moment the person who knows it is still on the page.
   */
  function mountCaptureForm(slot, product, car, reason = 'oos') {
    const tpl = document.getElementById('airflux-capture-template');
    if (!tpl || slot.childElementCount) return;
    const node = tpl.content.firstElementChild.cloneNode(true);
    node.dataset.product = product || '';
    node.dataset.car = car || '';
    // Read off the slot rather than threaded through every call site — the key
    // is what makes the panel's "who asked for this car" link resolve.
    node.dataset.carKey = slot.dataset.carKey || '';
    // Same journey as `carKey`: set by the state that knows, read by
    // airflux-capture.js, and never invented here.
    node.dataset.want = slot.dataset.want || '';
    // The catalogue's verdict on this car, travelling the same way — it is what
    // opens the panel's row correctly when the shopper refused analytics and
    // `/resolve` therefore never described the car. See `coverageAttrs`.
    node.dataset.coverage = slot.dataset.coverage || '';
    node.dataset.stocks = slot.dataset.stocks || '';
    node.dataset.reason = reason;

    // Same reasoning as `carKey` above: the question rides on the slot, so
    // `mountCaptures()` re-mounting a repainted result carries it too, and no
    // call site has to grow two more positional arguments it does not use.
    //
    // ⚠️ Only where the answer can actually be recorded. With no capture
    // endpoint the form falls back to Shopify's native customer form, which
    // carries a fixed set of fields and no free text — so the question would be
    // asked and the answer dropped, which is worse than not asking. The
    // endpoint travels on the template itself, from the theme setting.
    const askLabel = (tpl.content.firstElementChild.dataset.endpoint || '').trim() ? slot.dataset.askLabel || '' : '';
    if (askLabel) {
      const ask = node.querySelector('[data-airflux-capture-ask]');
      const askInput = node.querySelector('[data-airflux-capture-ask-input]');
      if (ask && askInput) {
        ask.hidden = false;
        ask.querySelector('[data-airflux-capture-ask-label]').textContent = askLabel;
        askInput.placeholder = slot.dataset.askPlaceholder || '';
        // Required ONLY where the sign-up would otherwise record nothing at
        // all. A customer who has just told us they cannot identify their own
        // version must never be blocked by a field asking them to.
        if (slot.dataset.askRequired === 'true') askInput.required = true;
      }
    }
    // 🔴 The SAME vocabulary the Worker writes, not a second one.
    //
    // This hidden field is the no-endpoint fallback's only way to label a
    // customer: with no Worker, the form posts natively and Shopify writes
    // whatever is in here. It used to write `airflux-coverage_gap` /
    // `airflux-oos` while the Worker wrote `airflux-car-request` /
    // `airflux-back-in-stock` for the same two things — so a record created by
    // the fallback carried a tag no filter and no panel link keys on, and sat
    // in the customer list under a label nobody would ever search for.
    //
    // The car key rides along for the same reason. It is the catalogue's own
    // handle — machine-made, never anything typed — so it is safe as a tag and
    // it is what every "who asked for this car" link opens. Without it a
    // fallback record belonged to no car at all.
    const tags = node.querySelector('[data-airflux-capture-tags]');
    if (tags) {
      const kind = reason === 'coverage_gap' ? 'airflux-car-request' : 'airflux-back-in-stock';
      const key = slot.dataset.carKey || '';
      tags.value =
        kind + (product ? `, product:${product}` : '') + (key ? `, car:${key}` : '');
    }
    // The template ships both labels (see airflux-capture-template.liquid), so
    // a coverage-gap form stops promising to email when stock returns.
    const submit = node.querySelector('[data-airflux-capture-submit]');
    const label = node.getAttribute(`data-label-${reason}`);
    if (submit && label) submit.textContent = label;
    slot.appendChild(node); // custom element upgrades on connect
  }

  if (!customElements.get('airflux-fitment')) customElements.define('airflux-fitment', AirfluxFitment);

  window.AirfluxCar = Car;

  /**
   * Shared so the resolved product-line card can raise a coverage-gap signal
   * with the SAME form the router's demand row raises it with — one template,
   * one set of tags, one shape of record for the client to read. The card mounts
   * it on the press rather than on paint, so it is always long after this file
   * has run.
   */
  window.AirfluxCapture = { mount: mountCaptureForm };
})();
